/* THE LUNCH CLUB — BUNDLE BUILDER

   Alpine store for all builder state, plus the Recharge / Shopify / Klaviyo adapters. Everything shop-specific
   arrives from Liquid through `#bb-config`; nothing is hardcoded here.

   BUNDLE STRUCTURE — two separate Shopify products, not one with twelve variants. The one-time bundle has ONE
   Recharge collection source (meals); the subscription bundle has TWO (meals + gifts), so its "4 Meals" variant
   has items_count = 5 because one slot is the gift. `purchaseType` picks the product, `boxSize` the variant.

   EXTERNAL CONTRACTS, verified against live vendor docs on 2026-09-01. Each cost time to establish; check the doc
   before changing any of them.

   * Recharge Storefront SDK 2.1.0 — global is lowercase `recharge`. For a fixed-price bundle `getBundleId()`
     returns the value that MUST ride on the single parent cart line as the `_rb_id` property; child selection
     lines are NOT added to the cart, and `selling_plan` is a sibling of `properties`. There is no
     `window.ReChargeBundles`, and inside a theme the settings path is `bundleData.loadBundleData()`, not
     `getCDNBundleSettings()` — both are supported below and normalised to one shape.
     https://storefront.rechargepayments.com/client/docs/examples/bundle/fixed_price_bundle/
   * Shopify Ajax Cart — `items[]` on cart/add.js, cart-level `attributes` on cart/update.js. Routes come from
     Liquid so market and locale prefixes are respected.
   * Klaviyo Client API — success is 202, not 200. Only `content-type` and `revision` survive the CORS preflight.
     Creating a profile does NOT grant marketing consent: the schema has no `subscriptions` field, which is what
     we want. https://developers.klaviyo.com/en/reference/create_client_profile */

(function () {
  'use strict';

  /* Warnings and errors always print: a misconfigured bundle is something whoever is looking at the page needs to
     see. `bbLog` is for the delivery-date clock alone (`logDeliveryClock`). Prefixed so it can be filtered in the
     console. */

  var LOG = '===>>>';

  function bbLog() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG);
    console.log.apply(console, args);
  }

  function bbWarn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG);
    console.warn.apply(console, args);
  }

  function bbError() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG);
    console.error.apply(console, args);
  }

  var STORAGE_KEY = 'tlc_builder_v1';
  var STORAGE_SCHEMA_VERSION = 2; // bumped: purchaseType now selects a product, frequency is a selling plan id
  var PERSIST_DEBOUNCE_MS = 300;
  var CLOCK_TICK_MS = 60000;

  // How long a page with a box in the cart waits to hear whether it is still there before showing it anyway.
  var CART_CHECK_TIMEOUT_MS = 3000;

  /* How long a failed checkout waits to learn which dishes are no longer available. The answer usually takes about
     2s (measured 2026-10-21); the rest is room for a slow phone connection. Past it, the customer is told the
     connection seems slow, since nothing about the box is known. */
  var AVAILABILITY_CHECK_TIMEOUT_MS = 10000;

  // Set in this tab on the way to checkout and taken on the way back, so the return opens on the review page.
  var HANDOFF_KEY = 'tlc_builder_handoff';

  // The longest the redirect to checkout waits for the page to finish loading — see `whenLoaded`.
  var LOAD_WAIT_MS = 5000;

  // How far below the viewport the end of a meal or add-on grid starts loading the next batch: about a row of
  // cards, so the next row is usually in place before the customer reaches it. See `watchGridEnd`.
  var GRID_LOAD_AHEAD_PX = 400;

  // Query parameters that name a box. A link carrying any of them speaks for the visit — see `applyUrlParams`.
  var BOX_URL_PARAMS = ['purchase_type', 'box_size', 'frequency'];

  /* Recharge's readable copy of the box, beside `_rb_id` on the bundle line. Its docs call `Contents` "required to
     ensure compatibility with other Recharge features" (https://docs.getrecharge.com/docs/line-item-properties-using-bundles),
     though the SDK example this builder followed sends `_rb_id` alone. Written exactly as Recharge's own widget writes it
     (`formatFixedPricedBundlePayload` / `selectionToString` in storefront-bundling-widget, read 2026-10-16): key
     "Contents", each selection "<qty>x <title>", joined by a single space. Recharge carries it onto the subscription
     and replaces it when the customer changes the box, and it gives the merchant the box in words on any order whose
     items Recharge failed to add (order #3410, 2026-10-16). */
  var BUNDLE_CONTENTS_PROPERTY = 'Contents';

  /* Marks a box checked out on the charge day after the cut-off, with the value 'true'. Recharge has no cut-off of
     its own here: it counts the checkout as the charge day's and charges again a week later, which would put a
     second box in the run this one already went to. A Recharge Automate flow matching this property moves that next
     charge on a week. Automate reads line item properties, not cart attributes, and matches name and value exactly,
     so neither may change without the flow. The underscore keeps it out of checkout and the cart. */
  var AFTER_CUTOFF_PROPERTY = '_after_cutoff';

  /* The Google Reviews Rocket app's own widget endpoint. Public, no key, and keyed by the shop's permanent
     `.myshopify.com` domain — a CUSTOM DOMAIN RETURNS 422.

     Called directly rather than embedding the app's widget, which renders its own inline-styled markup the design
     cannot use. The app stays installed and still decides which reviews the endpoint returns.

     Undocumented, so every call is best-effort: anything short of a well-formed payload with one usable review
     renders nothing. Reviews sit beside the box and must never block it. */
  var REVIEWS_ENDPOINT = 'https://widget-ms-grp.entanglecommerce.com/api/review-with-widgets';
  var REVIEWS_TIMEOUT_MS = 6000;
  var REVIEWS_MAX = 12;

  /* Three pages, not seven steps. `setupSections` is what the setup page reveals in order — presentation, not
     navigation, so it never enters the URL or history. */

  var ALL_STEPS = ['quiz', 'setup', 'meals', 'review'];

  // Three questions, on three pages. The email capture rides on the last one (2133:4122) rather than taking a
  // fourth page of its own, so the last screen both asks and finishes.
  var QUIZ_LAST_PAGE = 2;

  // Pages that cannot be rendered at all until Recharge has told us the bundle's rules.
  var STEPS_NEEDING_BUNDLE_DATA = ['meals', 'review'];

  // The original spec's per-step deep links stay valid: any ad URL already built keeps working.
  var LEGACY_STEP_MAP = {
    type: 'setup',
    size: 'setup',
    frequency: 'setup',
    gift: 'setup',
    addons: 'review'
  };

  function normaliseStep(value) {
    if (!value) return null;
    if (ALL_STEPS.indexOf(value) !== -1) return value;
    return LEGACY_STEP_MAP[value] || null;
  }

  /* --- Config --- */

  /* The browser's own parser rather than a list of replacements, so every entity is handled and none twice.
     A textarea's innerHTML never executes anything, which is why it is the safe element for this. */
  var entityDecoder = null;
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    if (!entityDecoder) entityDecoder = document.createElement('textarea');
    entityDecoder.innerHTML = value;
    return entityDecoder.value;
  }

  function decodeStrings(node) {
    if (typeof node === 'string') return decodeEntities(node);
    if (node && typeof node === 'object') {
      Object.keys(node).forEach(function (key) {
        node[key] = decodeStrings(node[key]);
      });
    }
    return node;
  }

  /* --- Allergens ---------------------------------------------------------------------------------------------

     The merchant lists allergens once in Theme settings (comma-separated), and an ingredient is shown bold, italic
     and underlined when it is one of them (client, 2026-10-12). Each entry of the dish's ingredients metafield is
     one ingredient, and so is each comma-separated item inside its brackets; the text before the brackets is one
     more. An entry listed whole, brackets and all, is marked whole. Every ingredient is compared whole, capitals
     ignored: words inside one are never matched on their own, which had marked the "Lait" of "Lait De Coco" as
     milk (client, 2026-10-21). Anything else, accents included, has to be typed the way the ingredient is written. */

  /** The listed allergens, lowercased; null when none are listed. */
  function allergenNames(list) {
    var names = String(list || '')
      .split(',')
      .map(function (name) {
        return name.trim().toLowerCase();
      })
      .filter(Boolean);
    return names.length ? new Set(names) : null;
  }

  function highlightAllergens(root, names) {
    if (!names || !root) return;

    var isListed = function (text) {
      return names.has(text.trim().toLowerCase());
    };

    // One ingredient's text, marked when it is a listed allergen. The spaces around it stay outside the mark.
    var piece = function (text) {
      var fragment = document.createDocumentFragment();
      var core = text.trim();
      if (!core || !isListed(core)) {
        fragment.appendChild(document.createTextNode(text));
        return fragment;
      }
      var start = text.indexOf(core);
      var mark = document.createElement('strong');
      mark.className = 'bb-allergen';
      mark.textContent = core;
      fragment.appendChild(document.createTextNode(text.slice(0, start)));
      fragment.appendChild(mark);
      fragment.appendChild(document.createTextNode(text.slice(start + core.length)));
      return fragment;
    };

    root.querySelectorAll('[data-bb-ingredients] [data-bb-ingredient]').forEach(function (entry) {
      var text = entry.textContent;
      var open = text.indexOf('(');
      var close = text.lastIndexOf(')');
      var fragment = document.createDocumentFragment();

      if (isListed(text) || open === -1 || close < open) {
        fragment.appendChild(piece(text));
      } else {
        fragment.appendChild(piece(text.slice(0, open)));
        fragment.appendChild(document.createTextNode('('));
        text
          .slice(open + 1, close)
          .split(',')
          .forEach(function (item, index) {
            if (index) fragment.appendChild(document.createTextNode(','));
            fragment.appendChild(piece(item));
          });
        fragment.appendChild(document.createTextNode(text.slice(close)));
      }

      entry.textContent = '';
      entry.appendChild(fragment);
    });
  }

  function readConfig() {
    var node = document.getElementById('bb-config');
    if (!node) {
      bbWarn('#bb-config is not in the DOM — the bundle-builder section did not render on this page.');
      return null;
    }

    var config;
    try {
      config = JSON.parse(node.textContent);
    } catch (error) {
      bbError('Could not parse #bb-config. The Liquid that builds it produced invalid JSON.', error);
      bbError('Raw #bb-config contents follow:', node.textContent);
      return null;
    }

    // Shopify's `t` filter HTML-escapes what it returns, so a translation containing an ampersand arrives here
    // as "Review &amp; pay". Alpine's x-text writes textContent, which does not decode entities — so without
    // this the step tab would literally read "Review &amp; pay". Decode once, on read.
    config.strings = decodeStrings(config.strings);

    reportConfigHealth(config);

    return config;
  }

  /* The commonest cause of `productId: 0` is not a blank setting: a `product` setting returns blank when the
     selection "isn't visible", so a bundle product left Unlisted looks filled in the theme editor while Liquid
     sees nothing. Worth saying out loud, because the setting looks correct. */
  function reportConfigHealth(config) {
    var bundles = config.bundles || {};
    var collections = (config.recharge && config.recharge.collections) || {};

    ['subscription', 'onetime'].forEach(function (which) {
      var bundle = bundles[which] || {};
      if (bundle.productId) {
        var unreadable = (bundle.variants || []).filter(function (variant) {
          return !variant.boxSize;
        });
        if (unreadable.length) {
          bbWarn(
            which + ' bundle: could not read a box size out of ' + unreadable.length + ' variant name(s):',
            unreadable.map(function (variant) {
              return variant.sizeLabel;
            }),
            '— these will not be offered. Check the product has an option named "Box Size" with a number in each value.'
          );
        }
      } else {
        bbWarn(
          which + ' bundle: Liquid resolved NO product (productId 0).',
          '\n  1. Is it picked in Theme settings -> Bundle builder · Setup?',
          '\n  2. If it IS picked, the product is almost certainly NOT PUBLISHED to the Online Store sales channel',
          '(the products list shows it as "Unlisted"). Shopify returns blank for a product setting whose selection',
          '"isn\'t visible", so Liquid cannot see it. Publish it to Online Store — Recharge needs that too, or',
          '/cart/add.js cannot add the variant either.'
        );
      }
    });

    Object.keys(collections).forEach(function (key) {
      if (!collections[key]) bbWarn('collection "' + key + '" is not set in Theme settings -> Bundle builder · Setup.');
    });

    // Real prices are shown exactly as checkout will charge them, so a box or add-on priced between 5-centime
    // steps is a pricing fix in Shopify, not something the builder can round away. Say so rather than hide it.
    if (config.currency === 'CHF') {
      var offStep = [];
      ['subscription', 'onetime'].forEach(function (which) {
        ((bundles[which] || {}).variants || []).forEach(function (variant) {
          if (variant.price % 5) offStep.push(which + ' ' + variant.sizeLabel + ': ' + variant.price / 100);
          (variant.sellingPlans || []).forEach(function (plan) {
            if (plan.price % 5) offStep.push(which + ' ' + variant.sizeLabel + ' on ' + plan.name + ': ' + plan.price / 100);
          });
        });
      });
      Object.keys(config.addons || {}).forEach(function (id) {
        var addon = config.addons[id];
        if (addon.price % 5) offStep.push('add-on ' + addon.title + ': ' + addon.price / 100);
      });
      if (offStep.length) {
        bbWarn('These CHF prices are not in 0.05 steps. Set them in Shopify; they are shown as charged:', offStep);
      }
    }
  }

  /* Floating local dates built from the server-rendered shop time. The device's absolute clock is never read,
     only its ELAPSED time since page load, because device clocks are wrong and customers travel (spec §7). */

  /** Parse `YYYY-MM-DDTHH:MM:SS` as a local (shop-timezone) wall-clock date, never as UTC. */
  /* Null when the string carries no offset — an older cached page — so the caller falls back to the wall clock.
     The offset is normalised to `+02:00` first: the bare form is not something every engine parses. */
  function parseInstant(value) {
    var match = /^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2})\s*([+-])(\d{2}):?(\d{2})$/.exec(String(value || '').trim());
    if (!match) return null;
    var date = new Date(match[1].replace(' ', 'T') + match[2] + match[3] + ':' + match[4]);
    return isNaN(date.getTime()) ? null : date;
  }

  /* The zone's wall clock, returned as a floating Date so the weekday arithmetic below stays plain whole-day maths
     and the cut-off a plain hour and minute. */
  function wallClockIn(instant, timeZone) {
    try {
      var parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(instant);
      var field = {};
      parts.forEach(function (part) { field[part.type] = part.value; });
      if (!field.year || !field.month || !field.day || !field.hour || !field.minute) return null;
      // `% 24`: an engine that ignores `hourCycle` can write the first hour of the day as 24.
      return new Date(
        Number(field.year), Number(field.month) - 1, Number(field.day), Number(field.hour) % 24, Number(field.minute)
      );
    } catch (error) {
      return null;  // an engine without this timezone: the caller falls back to shop time
    }
  }

  function parseFloating(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(String(value || ''));
    if (!match) return null;
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4] || 0),
      Number(match[5] || 0),
      Number(match[6] || 0)
    );
  }

  function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function addDays(date, days) {
    var next = new Date(date.getTime());
    next.setDate(next.getDate() + days);
    return next;
  }

  function toISODate(date) {
    var month = String(date.getMonth() + 1).padStart(2, '0');
    var day = String(date.getDate()).padStart(2, '0');
    return date.getFullYear() + '-' + month + '-' + day;
  }

  var WEEKDAY_ABBREVIATIONS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  /* `Thu 2026-11-05`, or `Thu 2026-11-05 15:00` with `withTime`, in English whatever the shop's language: for the
     console only. */
  function describeFloating(date, withTime) {
    var text = WEEKDAY_ABBREVIATIONS[date.getDay()] + ' ' + toISODate(date);
    if (!withTime) return text;
    return text + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0');
  }

  /* Recharge decides which day a checkout falls on in EASTERN TIME. So the weekday deciding which box you are in
     must be read on Recharge's clock, not the shop's: Zurich runs five or six hours ahead, so every day has a
     window where the two calendars name different days — and on a Friday morning in Switzerland that window is
     worth a whole week of delivery date. */
  var RECHARGE_TIMEZONE = 'America/New_York';

  var DEFAULT_CHARGE_WEEKDAY = 4;   // Thursday, in JavaScript's Sunday-first numbering
  var DEFAULT_DELIVERY_WEEKDAY = 2; // Tuesday
  var DEFAULT_CUTOFF_MINUTES = 15 * 60; // 15:00 on the charge day, Eastern Time

  /**
   * The delivery-date rule (spec §7): the charge day is the next charge weekday ON or after today, delivery the
   * next delivery weekday strictly after that. On the charge day itself, from the cut-off on, the charge day is
   * the following week's.
   *
   * Charge-driven on purpose — "the next Tuesday at least five days away" is wrong at the Thursday boundary. The
   * charge day comes from Recharge; the delivery day and the cut-off are theme settings, because Recharge schedules
   * the CHARGE and the charge day is not the day the box arrives.
   *
   * `now` is a floating wall-clock time on Recharge's clock. `cutoffMinutes` is minutes after midnight on that
   * clock; leave it out and the whole charge day counts. `cutoffTester` treats `now` as past the cut-off on the
   * charge day whatever the time, for a test customer (`snippets/bundle-builder-cutoff-tester.liquid`).
   */
  function nextDeliveryDate(now, options) {
    var opts = options || {};
    var chargeWeekday = typeof opts.chargeWeekday === 'number' ? opts.chargeWeekday : DEFAULT_CHARGE_WEEKDAY;
    var deliveryWeekday =
      typeof opts.deliveryWeekday === 'number' ? opts.deliveryWeekday : DEFAULT_DELIVERY_WEEKDAY;
    var cutoffMinutes = typeof opts.cutoffMinutes === 'number' ? opts.cutoffMinutes : null;

    /* The charge day itself still counts. Recharge bills the first order at checkout and, for a checkout on any
       other day, skips the coming charge day so nobody pays twice in a week; a checkout ON the charge day skips
       nothing. Either way the first box goes out with the coming charge day's run, and the next charge lands a
       full cycle later — so this box has to arrive before that one. */
    var today = startOfDay(now);
    var charge = addDays(today, (chargeWeekday - today.getDay() + 7) % 7);

    /* Until the cut-off. From then on the charge day's run is closed and the box goes with the next one, a week on.
       Recharge still sees a charge-day checkout and skips nothing, so the line is flagged (`AFTER_CUTOFF_PROPERTY`)
       for a Recharge flow to move the next charge on a week as well. */
    var minutesNow = now.getHours() * 60 + now.getMinutes();
    var afterCutoff =
      opts.cutoffTester === true ||
      (cutoffMinutes !== null && today.getDay() === chargeWeekday && minutesNow >= cutoffMinutes);
    if (afterCutoff) charge = addDays(charge, 7);

    // Delivery is the next delivery-weekday STRICTLY after the charge day. `|| 7` is load-bearing: when the two
    // weekdays are the same the modulo is 0, and delivery would otherwise land on the charge day itself.
    var daysChargeToDelivery = (deliveryWeekday - charge.getDay() + 7) % 7 || 7;
    var delivery = addDays(charge, daysChargeToDelivery);

    return { charge: charge, delivery: delivery, afterCutoff: afterCutoff };
  }

  function formatLongDate(date, locale) {
    try {
      return new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
    } catch (error) {
      return toISODate(date);
    }
  }

  /** "Tuesday" on its own — the cadence line reads "every Tuesday", not "every Tuesday 8 September". */
  function formatWeekday(date, locale) {
    try {
      return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
    } catch (error) {
      return '';
    }
  }

  /** "Tue 8 Sep" — the closing line under CHECKOUT, which Figma fits on one row (2269:86). British English
      shortens September to "Sept" where the frame, and every other month, has three letters. */
  function formatShortDate(date, locale) {
    try {
      var english = /^en\b/.test(locale);
      return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' })
        .formatToParts(date)
        .map(function (part) {
          return english && part.type === 'month' ? part.value.slice(0, 3) : part.value;
        })
        .join('');
    } catch (error) {
      return formatLongDate(date, locale);
    }
  }

  /** "8 September" — the menu window on the meals page, which names a week rather than a delivery. */
  function formatDayMonth(date, locale) {
    try {
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date);
    } catch (error) {
      return toISODate(date);
    }
  }

  /* Mirrors Shopify's `money` filter using the shop's own money_format. CHF commonly uses an apostrophe
     thousands separator, so that variant is supported. */

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (!isFinite(cents)) return '0';
    var value = (cents / 100).toFixed(precision);
    var parts = value.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(cents, format) {
    var amount = Number(cents) || 0;
    var pattern = format || '{{amount}}';
    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return formatWithDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return formatWithDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return formatWithDelimiters(amount, 2, "'", '.');
        case 'amount_no_decimals_with_apostrophe_separator':
          return formatWithDelimiters(amount, 0, "'", '.');
        case 'amount_with_space_separator':
          return formatWithDelimiters(amount, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return formatWithDelimiters(amount, 0, ' ', ',');
        default:
          return formatWithDelimiters(amount, 2, ',', '.');
      }
    });
  }

  /* `{{ name }}` is the translation files' placeholder; `[name]` is the one merchants type into the editable-text
     settings, where braces would read as Liquid. A bracketed word with no value is left as typed, so ordinary
     brackets in merchant copy survive. */
  function interpolate(template, values) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}|\[(\w+)\]/g, function (match, braced, bracketed) {
      var key = braced || bracketed;
      if (values[key] == null) return bracketed ? match : '';
      return String(values[key]);
    });
  }

  /* Rejects with TIMEOUT once `ms` pass without an answer. The request itself is not cancelled; only the waiting
     stops. */
  function withTimeout(promise, ms) {
    var timer = null;
    return Promise.race([
      promise,
      new Promise(function (resolve, reject) {
        timer = setTimeout(function () {
          reject(new Error('TIMEOUT'));
        }, ms);
      })
    ]).finally(function () {
      clearTimeout(timer);
    });
  }

  /** "A, B and C" / "A, B et C", joined the way the page's language joins a list. */
  function joinList(items, locale) {
    if (items.length < 2) return items.join('');
    try {
      return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
    } catch (error) {
      return items.join(', ');
    }
  }

  /* Shopify's `selling_plan` exposes no typed billing interval, so the cadence is read out of the plan's name.
     This is the one place in the build that reads meaning from merchant-authored text: FR and EN units are both
     recognised, a bare unit means 1, and anything unreadable is named in a console warning so a plan rename is
     visible rather than silent. */

  var UNIT_PATTERNS = [
    { unit: 'day', words: 'days?|jours?' },
    { unit: 'week', words: 'weeks?|semaines?' },
    { unit: 'month', words: 'months?|mois' },
    { unit: 'year', words: 'years?|ann(?:e)es?|ans?' }
  ];

  function stripAccents(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  function parseInterval(plan) {
    if (!plan) return null;

    var parts = [plan.name, plan.description];
    (plan.options || []).forEach(function (option) {
      parts.push(option.name, option.value);
    });

    var text = stripAccents(parts.filter(Boolean).join(' ').toLowerCase());

    // A number attached to a unit wins: "2 week subscription", "toutes les 2 semaines", "every 30 days".
    for (var i = 0; i < UNIT_PATTERNS.length; i += 1) {
      var withNumber = new RegExp('(\\d+)\\s*(?:' + UNIT_PATTERNS[i].words + ')\\b');
      var match = text.match(withNumber);
      if (match) return { unit: UNIT_PATTERNS[i].unit, count: parseInt(match[1], 10) };
    }

    // A bare unit means one of it: "week subscription" -> every week.
    for (var j = 0; j < UNIT_PATTERNS.length; j += 1) {
      var bare = new RegExp('\\b(?:' + UNIT_PATTERNS[j].words + ')\\b');
      if (bare.test(text)) return { unit: UNIT_PATTERNS[j].unit, count: 1 };
    }

    return null;
  }

  function intervalKey(interval) {
    return interval ? interval.unit + '-' + interval.count : '';
  }

  /* Recharge counts weekdays from MONDAY, JavaScript from SUNDAY. Passing one straight into the other shifts
     every delivery by a day, silently — hence its own function and its own test. Out of range returns null, so a
     bad value falls back rather than resolving to Sunday. */
  function rechargeWeekdayToJs(day) {
    if (typeof day !== 'number' || !isFinite(day) || day < 0 || day > 6) return null;
    return (day + 1) % 7;
  }

  /* --- Recharge adapter --- */

  var Recharge = {
    /** Wait for the SDK global. Resolves to null rather than throwing, so the caller can show a retry. */
    ready: function (timeoutMs) {
      var budget = timeoutMs || 10000;
      var startedAt = Date.now();
      var deadline = startedAt + budget;


      return new Promise(function (resolve) {
        (function poll() {
          if (window.recharge && window.recharge.bundle) {
            return resolve(window.recharge);
          }
          if (Date.now() > deadline) {
            if (window.recharge) {
              bbError(
                'window.recharge exists but has no `bundle` namespace after ' + budget + 'ms. Found:',
                Object.keys(window.recharge),
                '— the SDK version in Theme settings may be wrong.'
              );
            } else {
              bbError(
                'window.recharge never appeared after ' + budget + 'ms. The SDK script did not load.',
                '\n  Check: Theme settings -> Bundle builder · Setup -> "Recharge Storefront SDK script URL" is set,',
                'the script tag is in the page source, and it is not blocked (ad blocker / CSP / offline).'
              );
            }
            return resolve(null);
          }
          setTimeout(poll, 100);
        })();
      });
    },

    initialised: false,

    init: function (config) {
      if (this.initialised || !window.recharge || typeof window.recharge.init !== 'function') return;
      try {
        var args = { appName: 'TLC Bundle Builder', appVersion: '1.0.0' };
        // On Shopify `storeIdentifier` is optional, but passing it costs nothing and helps in preview contexts.
        if (config.shopDomain) args.storeIdentifier = config.shopDomain;
        // Only needed for the Storefront API namespace; harmless for bundle methods.
        if (config.recharge.storefrontToken) args.storefrontAccessToken = config.recharge.storefrontToken;
        window.recharge.init(args);
        this.initialised = true;
      } catch (error) {
        bbWarn('recharge.init failed', error);
      }
    },

    /* Recharge answers in two shapes and both are normalised here: `loadBundleData` (the theme path) gives
       `collections[]` with min/max, `getCDNBundleSettings` (headless) gives `option_sources[]` with
       quantity_min/max, which real payloads send as null when unset. */
    loadSettings: async function (bundleProductId) {
      var sdk = window.recharge;
      if (!sdk) throw new Error('RECHARGE_SDK_MISSING');
      if (!bundleProductId) throw new Error('BUNDLE_PRODUCT_NOT_CONFIGURED');

      var raw = null;
      var source = null;
      var lastError = null;

      if (sdk.bundleData && typeof sdk.bundleData.loadBundleData === 'function') {
        try {
          raw = await sdk.bundleData.loadBundleData(String(bundleProductId));
          source = 'loadBundleData';
        } catch (error) {
          lastError = error;
          bbWarn('loadBundleData threw, falling back to the CDN settings:', error);
        }
      } else {
        bbWarn('recharge.bundleData.loadBundleData is not available on this SDK build.');
      }

      if (!raw && sdk.cdn && typeof sdk.cdn.getCDNBundleSettings === 'function') {
        try {
          raw = await sdk.cdn.getCDNBundleSettings(String(bundleProductId));
          source = 'getCDNBundleSettings';
        } catch (error) {
          lastError = error;
          bbWarn('getCDNBundleSettings threw:', error);
        }
      }

      if (!raw || !Array.isArray(raw.variants)) {
        bbError(
          'Recharge returned no usable bundle data for product ' + bundleProductId + '. Raw response:',
          raw,
          '\n  Check: the bundle is ACTIVE in Recharge (a draft bundle may not be served),',
          'this product id is the bundle parent, and you are on the real storefront domain —',
          "Recharge's bundle methods only work on your Shopify storefront origin.",
          lastError ? '\n  Last error: ' : '',
          lastError || ''
        );
        var failure = new Error('BUNDLE_DATA_UNAVAILABLE');
        failure.cause = lastError;
        throw failure;
      }

      var byVariantId = {};
      raw.variants.forEach(function (variant) {
        var externalVariantId = String(variant.external_variant_id || variant.id || '');
        if (!externalVariantId) return;

        var collections = [];

        if (Array.isArray(variant.collections)) {
          // loadBundleData shape
          variant.collections.forEach(function (binding) {
            collections.push({
              id: String(binding.id),
              min: binding.min == null ? null : Number(binding.min),
              max: binding.max == null ? null : Number(binding.max)
            });
          });
        }

        if (Array.isArray(variant.option_sources)) {
          // getCDNBundleSettings shape
          variant.option_sources.forEach(function (optionSource) {
            collections.push({
              id: String(optionSource.collection_id),
              min: optionSource.quantity_min == null ? null : Number(optionSource.quantity_min),
              max: optionSource.quantity_max == null ? null : Number(optionSource.quantity_max)
            });
          });
        }

        // Total item count across every collection, used when a single-source bundle has no per-collection limits.
        var itemsCount = variant.items_count == null ? null : Number(variant.items_count);
        if (itemsCount == null && Array.isArray(variant.ranges) && variant.ranges.length) {
          var range = variant.ranges[0];
          var rangeMin = range.min != null ? range.min : range.quantity_min;
          if (rangeMin != null) itemsCount = Number(rangeMin);
        }

        byVariantId[externalVariantId] = {
          itemsCount: itemsCount,
          enabled: variant.enabled !== false,
          collections: collections
        };
      });


      return { source: source, byVariantId: byVariantId };
    },

    /* The charge day lives on the PLAN under `subscription_preferences`, and only exists when the merchant set
       "charge on a specific day of the week" — otherwise it is null. Read from Recharge so it is not maintained
       in two places. Best-effort: any failure resolves to an empty map and a reason rather than throwing,
       because a missing charge day must fall back rather than block the builder. */
    loadSchedule: async function () {
      var sdk = window.recharge;
      var empty = function (reason) {
        return { byExternalPlanId: {}, any: null, reason: reason };
      };

      if (!sdk) return empty('SDK missing');

      var planApi = sdk.plan || sdk.plans;
      if (!planApi || typeof planApi.listPlans !== 'function') return empty('this SDK build has no plan.listPlans');

      // A session is required even for the anonymous, product-scoped calls. The SDK has moved this around
      // between versions, so try what it offers and give up quietly rather than guessing at one name.
      var session = null;
      var auth = sdk.auth || {};
      var attempts = ['getSession', 'loginShopifyAppProxy', 'getStorefrontSession'];
      for (var i = 0; i < attempts.length && !session; i++) {
        if (typeof auth[attempts[i]] !== 'function') continue;
        try {
          session = await auth[attempts[i]]();
        } catch (error) {
        }
      }

      var response;
      try {
        response = await planApi.listPlans(session, { limit: 250 });
      } catch (error) {
        bbWarn('order schedule: listPlans failed, so the theme setting stays in charge.', error);
        return empty('listPlans failed');
      }

      var plans = (response && (response.plans || response.data)) || [];
      if (!Array.isArray(plans) || plans.length === 0) return empty('no plans returned');

      var byExternalPlanId = {};
      var any = null;
      var withCutoff = [];

      plans.forEach(function (plan) {
        var prefs = plan && plan.subscription_preferences;
        if (!prefs) return;

        /* A cut-off window changes which checkouts skip the coming charge, and the date rule above does not
           model one — so a plan carrying one is said out loud rather than dated as if it had none. */
        if (rechargeWeekdayToJs(prefs.cutoff_day_of_week) !== null) withCutoff.push(plan.external_plan_id);

        var schedule = { chargeWeekday: rechargeWeekdayToJs(prefs.order_day_of_week) };
        if (schedule.chargeWeekday === null) return;

        if (plan.external_plan_id != null) byExternalPlanId[String(plan.external_plan_id)] = schedule;
        if (!any) any = schedule;
      });

      if (withCutoff.length) {
        bbWarn('order schedule: these Recharge plans have a cut-off day, which the delivery date ignores:', withCutoff);
      }

      if (!any) {
        return empty('the plans carry no fixed charge day — Recharge has no schedule to give');
      }

      return { byExternalPlanId: byExternalPlanId, any: any, reason: null };
    },

    /* Every id is sent as a string — that is what `BundleSelectionAppProxy` declares. */
    /* Validation and minting are separate round trips to Recharge (about 1.4s and 1.3s, measured 2026-10-06) and
       neither needs the other's answer, so they run side by side. The id is only handed back once validation has
       passed: one minted for a selection that fails is simply never used. */
    getBundleId: async function (bundle) {
      var sdk = window.recharge;
      if (!sdk || !sdk.bundle) throw new Error('RECHARGE_SDK_MISSING');

      var minting = sdk.bundle.getBundleId(bundle);
      // A failed validation throws first; the mint may still reject afterwards, and must not surface unhandled.
      minting.catch(function () {});

      // `validateBundleSelection` is the theme-side validator and returns { valid, error }.
      if (typeof sdk.bundle.validateBundleSelection === 'function') {
        var result = await sdk.bundle.validateBundleSelection(bundle);
        if (result && result.valid !== true) {
          var selectionError = new Error('RECHARGE_VALIDATION_FAILED');
          selectionError.detail = result && result.error;
          throw selectionError;
        }
      } else if (typeof sdk.bundle.validateBundle === 'function') {
        // Older SDKs return `true` OR an error *string* — never use a truthiness check here.
        var validation = await sdk.bundle.validateBundle(bundle);
        if (validation !== true) {
          var validationError = new Error('RECHARGE_VALIDATION_FAILED');
          validationError.detail = typeof validation === 'string' ? validation : undefined;
          throw validationError;
        }
      }

      return minting;
    },

    /* The selections Recharge turns down because the dish is no longer on offer: gone from the collection it was
       picked from, or listed with its variant unavailable. Read fresh from the bundle data Recharge's validator
       reads (`/tools/recurring/bundle-data/<product>`, served no-store), by the test it applies — recharge-client
       2.1.0's `areSelectedProductsValid` rejects a product its collection no longer lists — so it names the dishes
       a failed validation was about. A collection missing from the data is a set-up problem rather than a dish, so
       nothing in it is named.

       Resolves to null when the data cannot be read, and rejects only with TIMEOUT, when it takes longer than
       `timeoutMs` to come. */
    unavailableSelections: async function (bundle, timeoutMs) {
      var sdk = window.recharge;
      if (!bundle || !sdk || !sdk.bundleData || typeof sdk.bundleData.loadBundleData !== 'function') return null;

      var data = null;
      try {
        data = await withTimeout(sdk.bundleData.loadBundleData(String(bundle.externalProductId)), timeoutMs);
      } catch (error) {
        if (error && error.message === 'TIMEOUT') throw error;
        bbWarn('Could not check which dishes are still available:', error);
        return null;
      }

      var collections = data && data.collections;
      if (!collections) return null;

      return bundle.selections.filter(function (selection) {
        var collection = collections[String(selection.collectionId)];
        if (!collection || !Array.isArray(collection.products)) return false;
        var product = collection.products.find(function (item) {
          return String(item.id) === String(selection.externalProductId);
        });
        if (!product) return true;
        var variant = (product.variants || []).find(function (item) {
          return String(item.id) === String(selection.externalVariantId);
        });
        return !variant || variant.available_for_sale === false;
      });
    }
  };

  /* --- Shopify cart adapter --- */

  function Cart(routes) {
    this.routes = routes;
  }

  Cart.prototype.post = async function (url, body) {

    var response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

    var payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok) {
      var cartError = new Error('CART_ERROR');
      cartError.status = response.status;
      // Shopify returns { status, message, description } on 4xx.
      cartError.detail = (payload && (payload.description || payload.message)) || null;
      bbError('POST ' + url + ' failed with ' + response.status + ':', payload);
      throw cartError;
    }

    return payload;
  };

  Cart.prototype.addItems = function (items) {
    return this.post(this.routes.cartAdd, { items: items });
  };

  /* Cart-level `attributes` and line `updates` in one request. Updates are keyed by LINE key, not variant id: a
     key names one line exactly, so a same-variant line the customer added from a product page, or one carrying
     other properties, is left alone (checked on the live store 2026-10-06). */
  Cart.prototype.update = function (body) {
    return this.post(this.routes.cartUpdate, body);
  };

  /* `timeoutMs` is for the reads a page waits on: a cart that never answers must not hold the builder hidden. */
  Cart.prototype.get = async function (timeoutMs) {
    var controller = timeoutMs && typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, timeoutMs) : null;
    try {
      var response = await fetch(this.routes.cart + '.js', {
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      });
      if (!response.ok) return null;
      return await response.json();
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  /* --- Klaviyo adapter (Client API — public key only, never a private key) --- */

  function Klaviyo(config) {
    this.publicKey = config.publicKey;
    this.revision = config.revision;
    this.locale = config.locale;
    this.enabled = Boolean(config.enabled && config.publicKey);
  }

  Klaviyo.prototype.request = async function (path, body) {
    if (!this.enabled) return { skipped: true };

    var url = 'https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(this.publicKey);

    // The CORS preflight only allows `content-type` and `revision`. Adding anything else breaks the call.
    // Wildcard ACAO + credentials is rejected by browsers, so credentials must stay at the default.
    var response = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: this.revision },
      body: JSON.stringify(body)
    });

    // Success is 202, not 200.
    if (response.status !== 202) {
      var error = new Error('KLAVIYO_ERROR');
      error.status = response.status;
      bbWarn('Klaviyo /' + path + ' returned ' + response.status + ' (202 expected).');
      throw error;
    }

    return { ok: true };
  };

  /* This endpoint has no `subscriptions` field, so it cannot grant marketing consent — the behaviour we want
     for CH/EU (spec §8). */
  Klaviyo.prototype.identify = function (email, properties) {
    var attributes = { email: email, properties: properties };
    // A profile field of its own, not a property: it is where Klaviyo looks for the language to write to them in.
    if (this.locale) attributes.locale = this.locale;
    return this.request('profiles', { data: { type: 'profile', attributes: attributes } });
  };

  Klaviyo.prototype.track = function (metricName, email, properties) {
    return this.request('events', {
      data: {
        type: 'event',
        attributes: {
          // `properties`, `metric` and `profile` are all required by the schema.
          properties: properties || {},
          metric: { data: { type: 'metric', attributes: { name: metricName } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }
      }
    });
  };

  /* Everything is coerced rather than trusted: a third-party response with no schema guarantee, so a missing
     `data`, a non-array `reviews` or an entry with no text must all end as "nothing to show". */

  function normaliseReviews(payload, max) {
    var data = payload && payload.data;
    var list = data && data.reviews;
    if (!Array.isArray(list)) return [];

    return list
      .map(function (review, index) {
        if (!review || typeof review !== 'object') return null;

        // Google keeps the shopper's own line breaks; the card is a single flowing quote, so collapse them.
        var text = String(review.text == null ? '' : review.text).replace(/\s+/g, ' ').trim();
        var author = String(review.author_name == null ? '' : review.author_name).trim();
        var rating = Math.round(Number(review.rating));

        if (!text || !author) return null;

        return {
          id: String(review.id || review.time || index),
          author: author,
          text: text,
          // Out-of-range or unparseable ratings fall back to a full card rather than drawing nothing.
          rating: rating >= 1 && rating <= 5 ? rating : 5
        };
      })
      .filter(Boolean)
      .slice(0, max);
  }

  /** The saved builder state, or null. Never throws: private modes refuse storage, and nothing depends on it. */
  function readSaved() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function markHandoff() {
    try {
      sessionStorage.setItem(HANDOFF_KEY, '1');
    } catch (error) {
      /* storage refused: the return simply opens wherever its URL says */
    }
  }

  /** Whether this tab just came back from checkout. Reading it clears it, so it answers once. */
  function takeHandoff() {
    try {
      var set = sessionStorage.getItem(HANDOFF_KEY) === '1';
      sessionStorage.removeItem(HANDOFF_KEY);
      return set;
    } catch (error) {
      return false;
    }
  }

  /* A script navigation made before the page has finished loading REPLACES the current history entry rather
     than adding one (the HTML spec's rule when there is no fresh user gesture, and the click on Checkout is long
     spent once Recharge and the cart have answered). Replacing the review page's entry is what sent Back from
     checkout to the meals page — reproduced 2026-10-06 in Chrome on a review page reloaded a few seconds before.
     So wait for `load`, plus one task, since a document only counts as loaded once its load event has run. Never
     past the cap, though: a third-party script that never finishes loading must not strand the customer. */
  function whenLoaded(capMs) {
    return new Promise(function (resolve) {
      if (document.readyState === 'complete') {
        setTimeout(resolve, 0);
        return;
      }
      var timer = setTimeout(resolve, capMs);
      window.addEventListener(
        'load',
        function () {
          clearTimeout(timer);
          setTimeout(resolve, 0);
        },
        { once: true }
      );
    });
  }

  /* --- Store --- */

  function buildStore(config) {
    var cart = new Cart(config.routes);
    var klaviyo = new Klaviyo(config.klaviyo);

    // Absolute shop time at render, plus the device's anchor. We only ever use the device delta.
    var serverAnchor = parseFloating(config.serverNow) || new Date();
    /* The same moment as an absolute instant, so the day can be judged on Recharge's clock rather than the
       shop's. Null on a page rendered before `%z` was added, which is why every read of it is guarded. */
    var instantAnchor = parseInstant(config.serverNow);
    var clientAnchorMs = Date.now();

    // Liquid's `newline_to_br | split` leaves stray whitespace on each entry — normalise once, here.
    // The design prints the per-meal figure without a currency ("16.90 / MEAL"), so keep the shop's own
    // separator style but drop the currency text around it. Extracting the placeholder rather than stripping
    // letters means a shop formatting as "1'234.56 CHF" works as well as one formatting as "CHF 1'234.56".
    var amountOnlyFormat = (function () {
      var match = /\{\{\s*\w+\s*\}\}/.exec(config.moneyFormat || '');
      return match ? match[0] : '{{amount}}';
    })();

    /* Swiss prices go in steps of 5 centimes. The box, add-on and total prices are the variant prices checkout
       charges, so they are shown exactly as set; only a figure the builder works out itself (a box price shared
       per meal) can fall between steps, and those are rounded to the nearest 0.05. Any other currency keeps its
       cents. */
    var priceStep = config.currency === 'CHF' ? 5 : 1;

    function roundToPriceStep(cents) {
      return Math.round(cents / priceStep) * priceStep;
    }

    var persistTimer = null;

    // Built once from Theme settings; null when no allergens are listed. See `highlightAllergens`.
    var allergens = allergenNames(config.allergens);

    // Per grid, set while cards it just showed or hid are waiting to be laid out — see `checkGridEndSoon`.
    var gridRevealPending = { meal: false, addon: false };

    return {
      /* --- persisted state -------------------------------------------------------------------------------- */
      // Overwritten during init() by the first VISIBLE step, so a fresh visitor with the quiz enabled lands on
      // the quiz rather than skipping straight past it. `setup` is only the fallback.
      step: 'setup',
      // The step the customer asked for — restored from storage or deep-linked. Kept because boot has to clamp
      // before Recharge has answered, and `clampStep` only ever moves backwards: without the original ask there
      // is nothing to return to once the limits arrive.
      requestedStep: null,
      quiz: {
        goal: null,
        orderingFor: null,
        mealOccasion: null,
        email: null,
        completedAt: null,
        // Whether Klaviyo has the finished quiz. False for "Continue without email", for capture switched off and
        // for a call that failed or was blocked — see `cartAttributes`.
        klaviyoSent: false,
        skipped: false
      },
      purchaseType: null, // 'onetime' | 'subscription' — selects WHICH BUNDLE PRODUCT
      boxSize: null, // selects the variant within that product
      sellingPlanId: null, // subscription only; the Shopify selling plan id, read from the variant
      gift: null,
      meals: [],
      addons: [],

      /* What this box last put in the cart: `{ rbId, addons: [{ variantId, sellingPlanId, quantity }] }`, or null.
         The box is no longer cleared at checkout, so a customer who comes back to change something edits the
         order already in the cart, and the next checkout takes these lines out before adding the new ones. */
      cartLink: null,

      /* --- transient state -------------------------------------------------------------------------------- */
      deliveryDate: null, // never persisted — always recomputed (spec §4)

      // Set on the way out to checkout, so the back/forward cache handing this page back can be told apart from
      // any other return to it.
      handedToCheckout: false,

      // The order schedule as Recharge enforces it. Never persisted either: it is Recharge's to change, and a
      // stale copy in someone's localStorage would quietly outrank the live value.
      schedule: { state: 'idle', byExternalPlanId: {}, any: null, reason: null },
      chargeDate: null,
      afterCutoff: false, // recomputed with the date, never persisted
      ready: false,
      submitting: false,
      error: null,
      activeFilters: [],
      filterCounts: {},
      variantChoice: {},
      panel: { open: false, variantId: null, title: '', kind: 'meal', collectionId: '', productId: '', trigger: null },
      pushedStates: 0,
      releaseFocusTrap: null,
      quizPage: 0,

      // Batches are purely client-side: every card is already in the DOM (Liquid rendered it), so this only
      // controls how many of them are revealed (see `watchGridEnd`). No refetch, and filtering still sees the
      // whole menu.
      mealsShown: config.grid.mealsPerLoad,
      addonsShown: config.grid.addonsPerLoad,
      mealOrder: {},
      addonOrder: {},

      // Meals removed by shrinking the box, held so growing it again can put them back. Deliberately transient:
      // it is a within-session convenience, not part of the saved box.
      mealMatchCount: 0,
      addonCount: 0,

      // Mobile only. The desktop summary rail is hidden under 1000px, so "View box" opens it as a sheet.
      boxSheetOpen: false,

      // One entry per bundle product. `state` is 'idle' | 'loading' | 'ready' | 'error'.
      bundleData: {
        onetime: { state: 'idle', byVariantId: null, source: null, error: null },
        subscription: { state: 'idle', byVariantId: null, source: null, error: null }
      },

      /* How many the panel's add buttons will put in the box. Only meaningful while the item is NOT in the
         box: once it is, the panel's stepper edits the real quantity, exactly as the card's does. */
      panelDraftQty: 1,

      // Google reviews. `state` is 'idle' | 'loading' | 'ready' | 'empty' — there is deliberately no 'error':
      // nothing to show and something went wrong look identical to a shopper, so they render identically too.
      reviews: { state: 'idle', items: [] },

      /* Someone has explicitly asked for the quiz this visit — the button, or a `?step=quiz` link. It outranks
         every reason the quiz would otherwise be out of the flow. NOT persisted: asking is a per-visit thing. */
      quizRequested: false,

      /* --- Boot --- */

      /* A saved box that already went to the cart is checked against the cart before it is restored. Gone from
         there — bought, most likely, or removed in the cart drawer — it is finished with, and the visit starts
         from nothing, as every visit after a checkout used to. Still there, it is restored as it stands, ready to
         be edited and checked out again. The page stays hidden (`ready`) for that one read and no longer. */
      init: function () {
        var self = this;
        var saved = readSaved();

        // Every step change lands at the top of the step, the browser's own back and forward included. Left to
        // the browser, coming back from checkout reopened each step at whatever depth it was last scrolled to.
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

        if (!saved || !saved.cartLink) {
          this.boot(saved);
          return;
        }

        this.isLinkInCart(saved.cartLink).then(function (inCart) {
          if (inCart === false) {
            self.clearStorage();
            saved = null;
          }
          self.boot(saved);
        });
      },

      boot: function (saved) {
        var self = this;

        this.restore(saved);
        var linkNamedABox = this.applyUrlParams();

        // Back from checkout opens on the review page whatever the entry's URL says: a browser that replaced the
        // review page's history entry would otherwise land the customer on the page before it (client feedback,
        // 2026-10-06). `clampStep` below still moves them back if the box no longer qualifies.
        if (takeHandoff() && this.cartLink && !linkNamedABox) {
          this.requestedStep = 'review';
          this.step = 'review';
        }

        // Nothing restored and nothing deep-linked: open on the first step that is actually in the flow. The
        // quiz is the primary entry point from the homepage, so it must not be skipped by default.
        if (!this.requestedStep) this.step = this.visibleSteps[0];

        this.seedVariantChoices();
        this.indexFilterCounts();
        this.reindexGrids();
        this.recomputeDeliveryDate('page load');
        this.normalise();

        // `assumeLimits` because Recharge has not answered yet — see `canEnter`.
        this.step = this.clampStep(this.step, { assumeLimits: true });
        this.replaceUrl();

        this.ready = true;

        // Keep the displayed date honest if Thursday rolls over while the page sits open (spec §7).
        setInterval(function () {
          self.recomputeDeliveryDate('clock');
        }, CLOCK_TICK_MS);

        window.addEventListener('popstate', function (event) {
          var step = normaliseStep(event.state && event.state.bbStep) || self.readStepFromUrl() || 'setup';
          var previous = self.step;
          self.requestedStep = null;
          self.step = self.clampStep(step);
          self.pushedStates = Math.max(0, self.pushedStates - 1);
          self.closePanel();
          // Only on a real step change: a `#fragment` link fires popstate too, and must keep its own scroll.
          if (self.step !== previous) self.scrollToTop({ instant: true });
        });

        document.addEventListener('keydown', function (event) {
          if (event.key !== 'Escape') return;
          if (self.panel.open) self.closePanel();
          else if (self.boxSheetOpen) self.closeBoxSheet();
        });

        // Back from checkout, the browser can hand back this very page from its back/forward cache, frozen at
        // "Adding to your basket…": the redirect left while `submitting` was still true. Nothing is in flight
        // any more, so the button comes back.
        window.addEventListener('pageshow', function (event) {
          if (!event.persisted) return;
          self.submitting = false;

          // The frozen page keeps its old scroll, so put the customer back at the top of the review page.
          if (self.handedToCheckout) {
            self.handedToCheckout = false;
            takeHandoff();
            if (self.step !== 'review' && self.canEnter('review')) {
              self.step = 'review';
              self.replaceUrl();
            }
            self.scrollToTop({ instant: true });
          }

          // The box may have been bought since the page was frozen. If so, start over the way a fresh load would.
          var link = self.cartLink;
          if (!link) return;
          self.isLinkInCart(link).then(function (inCart) {
            if (inCart !== false || self.cartLink !== link) return;
            self.clearStorage();
            window.location.reload();
          });
        });

        // If a purchase type is already known (restored or deep-linked), start fetching its rules now.
        if (this.purchaseType) this.ensureBundleData(this.purchaseType);

        this.loadReviews();
      },

      /* --- Google reviews --- */

      // The carousel renders only when this is true, so a failed, slow or empty fetch simply leaves no trace.
      get hasReviews() {
        return this.reviews.state === 'ready' && this.reviews.items.length > 0;
      },

      loadReviews: function () {
        var self = this;
        var settings = config.reviews || {};

        if (!settings.enabled) return;
        if (!config.shopDomain) return;
        if (typeof fetch !== 'function') return;

        this.reviews.state = 'loading';

        // Abort rather than let a stalled request hold the state at 'loading' forever.
        var controller = typeof AbortController === 'function' ? new AbortController() : null;
        var timer = setTimeout(function () {
          if (controller) controller.abort();
        }, REVIEWS_TIMEOUT_MS);

        var settle = function (items) {
          clearTimeout(timer);
          self.reviews.items = items;
          self.reviews.state = items.length ? 'ready' : 'empty';
        };

        fetch(REVIEWS_ENDPOINT + '?shop=' + encodeURIComponent(config.shopDomain), {
          signal: controller ? controller.signal : undefined,
          headers: { Accept: 'application/json' },
          credentials: 'omit'
        })
          .then(function (response) {
            if (!response.ok) throw new Error('reviews HTTP ' + response.status);
            return response.json();
          })
          .then(function (payload) {
            settle(normaliseReviews(payload, settings.max || REVIEWS_MAX));
          })
          .catch(function () {
            // Offline, aborted, 4xx/5xx, or HTML where JSON was promised — all the same outcome.
            settle([]);
          });
      },

      /* --- Recharge bundle rules --- */

      bundleFor: function (purchaseType) {
        return (config.bundles && config.bundles[purchaseType]) || null;
      },

      get activeBundle() {
        return this.purchaseType ? this.bundleFor(this.purchaseType) : null;
      },

      /* The purchase type the setup page is drawn for. Before one is chosen, sizes are shown greyed at the
         subscription's prices — that is the offer the sections below describe — and the choice reprices them. */
      get displayType() {
        if (this.purchaseType) return this.purchaseType;
        return this.bundleFor('subscription') ? 'subscription' : 'onetime';
      },

      get displayBundle() {
        return this.bundleFor(this.displayType);
      },

      get dataEntry() {
        return this.purchaseType ? this.bundleData[this.purchaseType] : null;
      },

      get dataState() {
        var entry = this.dataEntry;
        return entry ? entry.state : 'idle';
      },

      get dataReady() {
        return this.dataState === 'ready';
      },

      /* The setup page is deliberately NOT gated: its cards must be visible before anything loads, and its sizes
         and prices come from Liquid. Only the meals count needs Recharge, so it reports that wait inline. */
      get needsBundleData() {
        return STEPS_NEEDING_BUNDLE_DATA.indexOf(this.step) !== -1;
      },

      get isBlockedOnData() {
        return this.needsBundleData && !this.dataReady;
      },

      /** On the setup page: a bundle is chosen and we are still waiting on its rules. */
      get setupDataPending() {
        return this.step === 'setup' && Boolean(this.purchaseType) && (this.dataState === 'loading' || this.dataState === 'idle');
      },

      get setupDataFailed() {
        return this.step === 'setup' && Boolean(this.purchaseType) && this.dataState === 'error';
      },

      /* The builder deliberately BLOCKS rather than guessing when this fails: with two differently-shaped
         bundles, a wrong meals limit would let someone build a box Recharge will reject. */
      ensureBundleData: async function (purchaseType, options) {
        var entry = this.bundleData[purchaseType];
        if (!entry) return;
        if (entry.state === 'loading') return;
        if (entry.state === 'ready' && !(options && options.force)) return;

        var bundle = this.bundleFor(purchaseType);
        entry.state = 'loading';
        entry.error = null;


        try {
          if (!bundle || !bundle.productId) throw new Error('BUNDLE_PRODUCT_NOT_CONFIGURED');

          var sdk = await Recharge.ready();
          if (!sdk) throw new Error('RECHARGE_SDK_MISSING');
          Recharge.init(config);

          // Deliberately not awaited: the charge day is a nicety, the meals limits are not. If the schedule call
          // is slow or unavailable the builder carries on with the built-in one.
          this.ensureSchedule();

          var settings = await Recharge.loadSettings(bundle.productId);
          entry.byVariantId = settings.byVariantId;
          entry.source = settings.source;
          entry.state = 'ready';

          // Limits are only known now, so re-check where the customer is allowed to stand — starting from the
          // step they ASKED for, not from wherever boot parked them. The ask has now been settled either way.
          this.step = this.clampStep(this.requestedStep || this.step);
          this.requestedStep = null;

        } catch (error) {
          entry.state = 'error';
          entry.error = (error && error.message) || 'BUNDLE_DATA_UNAVAILABLE';

          if (entry.error === 'BUNDLE_PRODUCT_NOT_CONFIGURED') {
            bbError(
              'BLOCKED: no ' + purchaseType + ' bundle product is visible to Liquid, so the meals count cannot be',
              'established and the builder refuses to guess.',
              '\n  If the product IS picked in Theme settings, publish it to the Online Store sales channel —',
              'it currently reads as "Unlisted", and Shopify hides an invisible selection from Liquid entirely.'
            );
          } else {
            bbError('BLOCKED: could not load the ' + purchaseType + ' bundle rules.', error);
          }
        }
      },

      retryBundleData: function () {
        if (this.purchaseType) this.ensureBundleData(this.purchaseType, { force: true });
      },

      /* How the meals count was decided, per variant — the most confusing number in this build, because on the
         subscription bundle "4 Meals" is 5 items and one is the gift. */
      explainMealsLimits: function (purchaseType) {
        var entry = this.bundleData[purchaseType];
        var bundle = this.bundleFor(purchaseType);
        if (!entry || !entry.byVariantId || !bundle) return null;

        var mealsCollectionId = String(config.recharge.collections.meals);

        return bundle.variants.map(function (variant) {
          var rules = entry.byVariantId[String(variant.id)];
          if (!rules) {
            return {
              variant: variant.sizeLabel,
              verdict: 'NO RULES — Recharge returned nothing for variant ' + variant.id
            };
          }

          var binding = rules.collections.find(function (item) {
            return item.id === mealsCollectionId;
          });

          var verdict;
          if (binding && (binding.min != null || binding.max != null)) {
            verdict = 'meals collection limit ' + binding.min + '-' + binding.max;
          } else if (rules.collections.length <= 1 && rules.itemsCount) {
            verdict = 'single collection source, so items_count (' + rules.itemsCount + ') IS the meals count';
          } else {
            verdict =
              'CANNOT DECIDE — ' + rules.collections.length + ' collection sources and no per-collection limits. ' +
              'Set a quantity min/max on the meals collection in the Recharge bundle, and check the meals ' +
              'collection id (' + mealsCollectionId + ') matches one of ' +
              rules.collections.map(function (c) { return c.id; }).join(', ');
          }

          return {
            variant: variant.sizeLabel,
            itemsCount: rules.itemsCount,
            collections: rules.collections,
            verdict: verdict
          };
        });
      },

      get dataErrorMessage() {
        var entry = this.dataEntry;
        if (!entry || entry.state !== 'error') return '';
        switch (entry.error) {
          case 'RECHARGE_SDK_MISSING':
            return config.strings.errorRechargeMissing;
          case 'BUNDLE_PRODUCT_NOT_CONFIGURED':
            return config.strings.errorBundleNotConfigured;
          default:
            return config.strings.errorBundleData;
        }
      },

      /* --- Clock --- */

      shopNow: function () {
        return new Date(serverAnchor.getTime() + (Date.now() - clientAnchorMs));
      },

      /* The absolute instant, or null when the page predates the offset. `shopNow` is only right as a wall clock:
         it is the shop's time written on the device's calendar, so converting it to UTC is off by the difference
         between the device's zone and the shop's. */
      instantNow: function () {
        return instantAnchor ? new Date(instantAnchor.getTime() + (Date.now() - clientAnchorMs)) : null;
      },

      /* Recharge's wall clock: Eastern, because that is where Recharge draws the day boundary. Null when the page
         predates the offset or the browser cannot resolve the zone. */
      rechargeNow: function () {
        var instantNow = this.instantNow();
        return instantNow ? wallClockIn(instantNow, RECHARGE_TIMEZONE) : null;
      },

      /* Failure is not an error state: the built-in charge day is a complete answer on its own, so this only
         ever upgrades it and never blocks on it. */
      ensureSchedule: async function () {
        if (this.schedule.state !== 'idle') return;
        this.schedule.state = 'loading';

        var result = await Recharge.loadSchedule();

        this.schedule.byExternalPlanId = result.byExternalPlanId;
        this.schedule.any = result.any;
        this.schedule.reason = result.reason;
        this.schedule.state = result.any ? 'ready' : 'unavailable';

        // The date on screen was computed from the theme setting; recompute now that Recharge has answered.
        this.recomputeDeliveryDate('Recharge schedule loaded');
      },

      /* The CHARGE day. Kept distinct from the delivery day on purpose: treating it as the delivery day would
         promise the box days before it ships.

         The chosen plan's, else the first subscription box plan Recharge has a charge day for: a one-time box has
         no plan, but it goes out with the same weekly run. Only box plans — add-ons carry plans of their own in
         Recharge, and those are not what decides the box's week. */
      get rechargeChargeWeekday() {
        if (this.schedule.state !== 'ready') return null;

        var byPlan = this.schedule.byExternalPlanId;
        var planIds = [this.sellingPlanId];
        var bundle = this.bundleFor('subscription');
        ((bundle && bundle.variants) || []).forEach(function (variant) {
          (variant.sellingPlans || []).forEach(function (plan) {
            planIds.push(plan.id);
          });
        });

        for (var i = 0; i < planIds.length; i++) {
          var schedule = planIds[i] != null && byPlan[String(planIds[i])];
          if (schedule) return schedule.chargeWeekday;
        }
        return null;
      },

      get effectiveChargeWeekday() {
        var fromRecharge = this.rechargeChargeWeekday;
        return typeof fromRecharge === 'number' ? fromRecharge : DEFAULT_CHARGE_WEEKDAY;
      },

      /* Recharge cannot answer this — it schedules the charge — so the delivery day is a theme setting. */
      get effectiveDeliveryWeekday() {
        var fromSettings = config.delivery.deliveryWeekday;
        return typeof fromSettings === 'number' ? fromSettings : DEFAULT_DELIVERY_WEEKDAY;
      },

      /* Recharge has no time-of-day cut-off for a charge day, so this is a theme setting too: minutes after midnight
         on the charge day, Eastern Time. A full day (1440) means none. */
      get effectiveCutoffMinutes() {
        var fromSettings = config.delivery.cutoffMinutes;
        return typeof fromSettings === 'number' ? fromSettings : DEFAULT_CUTOFF_MINUTES;
      },

      /** Diagnostics only: name a JS weekday number so nobody has to count from Sunday in their head. */
      weekdayName: function (weekday) {
        if (typeof weekday !== 'number') return '—';
        // 2024-01-07 was a Sunday, so adding the index lands on the weekday we want.
        var date = new Date(Date.UTC(2024, 0, 7 + weekday));
        try {
          return new Intl.DateTimeFormat(config.dateLocale, { weekday: 'long', timeZone: 'UTC' }).format(date);
        } catch (error) {
          return String(weekday);
        }
      },

      /* `reason` names the caller in the console line; the minute tick ('clock') logs only when the answer moves. */
      recomputeDeliveryDate: function (reason) {
        /* Off Recharge's clock the date falls back to shop time — a date an hour out beats no date — but the cut-off
           does not apply: shop time would put it hours early, and a flagged box has its next charge moved. A test
           customer is past it either way. */
        var rechargeNow = this.rechargeNow();
        var result = nextDeliveryDate(rechargeNow || this.shopNow(), {
          chargeWeekday: this.effectiveChargeWeekday,
          deliveryWeekday: this.effectiveDeliveryWeekday,
          cutoffMinutes: rechargeNow ? this.effectiveCutoffMinutes : null,
          cutoffTester: config.delivery.cutoffTester === true
        });
        var iso = toISODate(result.delivery);
        var changed = iso !== this.deliveryDate || result.afterCutoff !== this.afterCutoff;
        if (iso !== this.deliveryDate) this.deliveryDate = iso;
        this.chargeDate = toISODate(result.charge);
        this.afterCutoff = result.afterCutoff;
        if (reason !== 'clock' || changed) this.logDeliveryClock(reason, rechargeNow, result);
      },

      /* One console line with the Eastern-Time clock behind the date and the cut-off, so both can be checked in the
         browser. The instant is UTC, the moment everything else is worked out from. */
      logDeliveryClock: function (reason, rechargeNow, result) {
        var instantNow = this.instantNow();
        var cutoff = this.effectiveCutoffMinutes;
        var cutoffText = cutoff >= 1440
          ? 'none'
          : String(Math.floor(cutoff / 60)).padStart(2, '0') + ':' + String(cutoff % 60).padStart(2, '0') + ' ET';
        bbLog(
          'Delivery date (' + (reason || 'update') + '):',
          'Eastern Time ' + (rechargeNow ? describeFloating(rechargeNow, true) : 'unavailable, shop time used, no cut-off'),
          '| instant ' + (instantNow ? instantNow.toISOString() : 'unknown'),
          '| charge day ' + WEEKDAY_ABBREVIATIONS[this.effectiveChargeWeekday] +
            (typeof this.rechargeChargeWeekday === 'number' ? ' (Recharge)' : ' (built-in, Recharge not loaded yet)'),
          '| cut-off ' + cutoffText,
          '| test customer ' + (config.delivery.cutoffTester === true ? 'yes' : 'no'),
          '| past cut-off ' + (result.afterCutoff ? 'YES' : 'no'),
          '→ charge ' + describeFloating(result.charge) + ', delivery ' + describeFloating(result.delivery)
        );
      },

      get deliveryDateLabel() {
        if (!this.deliveryDate) return '';
        return formatLongDate(parseFloating(this.deliveryDate), config.dateLocale);
      },

      get deliveryDateShortLabel() {
        if (!this.deliveryDate) return '';
        return formatShortDate(parseFloating(this.deliveryDate), config.dateLocale);
      },

      /* --- Steps and routing --- */

      get skipQuiz() {
        if (this.quizRequested) return false;
        return Boolean(config.skipQuiz || this.quiz.completedAt || this.quiz.skipped);
      },

      get visibleSteps() {
        var self = this;
        return ALL_STEPS.filter(function (step) {
          if (step === 'quiz') return !self.skipQuiz;
          return true;
        });
      },

      /** The tabs shown in the progress bar — the quiz is a lead-in, not one of the three builder tabs. */
      get pageTabs() {
        return ['setup', 'meals', 'review'];
      },

      /* Every reached tab is filled, so the bar reads as progress rather than as a selector. */
      tabReached: function (index) {
        return this.pageTabs.indexOf(this.step) >= index;
      },

      /* Frequency and gift belong to the subscription bundle only — the one-time product has neither. */
      get setupSections() {
        var self = this;
        return ['type', 'size', 'frequency', 'gift'].filter(function (section) {
          if (section === 'frequency' || section === 'gift') return self.purchaseType === 'subscription';
          return true;
        });
      },

      /** Every setup section, in order, whether or not this purchase type can answer it. */
      get allSetupSections() {
        return ['type', 'size', 'frequency', 'gift'];
      },

      /** The numbering runs over all four, so "3." and "4." keep their numbers while they sit inactive. */
      sectionNumber: function (section) {
        return this.allSetupSections.indexOf(section) + 1;
      },

      /* All four sections are on screen from the start: sizes, cadences and the free gift are what a subscription
         is, so they are not held back behind earlier answers (client feedback, 2026-10-06 — they used to reveal
         one at a time). A section that cannot be answered yet is greyed and inert instead: everything after the
         first until a purchase type is chosen, and frequency and gift for good on a one-time box, so the
         customer can see what switching to a subscription would add. Nothing auto-advances. */
      isSectionInactive: function (section) {
        if (section === 'type') return false;
        if (!this.purchaseType) return true;
        if (section !== 'frequency' && section !== 'gift') return false;
        return this.purchaseType === 'onetime';
      },

      isSectionComplete: function (section) {
        switch (section) {
          case 'type':
            return Boolean(this.purchaseType);
          case 'size':
            return Boolean(this.boxSize);
          case 'frequency':
            return Boolean(this.sellingPlanId);
          case 'gift':
            return Boolean(this.gift);
          default:
            return false;
        }
      },

      get setupComplete() {
        var self = this;
        return this.setupSections.every(function (section) {
          return self.isSectionComplete(section);
        });
      },

      /* Counted over `pageTabs`, not `visibleSteps`: the quiz is a lead-in, not one of the three builder pages.
         Counting visible steps made the bar say "of 4" whenever the quiz was still ahead. */
      get stepIndex() {
        return Math.max(0, this.pageTabs.indexOf(this.step));
      },

      get stepNumber() {
        return this.stepIndex + 1;
      },

      get stepCount() {
        return this.pageTabs.length;
      },

      isStep: function (step) {
        return this.step === step;
      },

      /** Tab label suffix: "· 4 OF 6" while filling the box, "✓" once a page is done. */

      /* Rests on the customer's own selections, never on async Recharge state — otherwise a deep link gets
         clamped backwards while data is still in flight. `review` is the exception, because whether the box is
         full depends on a meals count only Recharge knows; `assumeLimits` is how boot asks before the answer
         exists. */
      canEnter: function (step, options) {
        switch (step) {
          case 'quiz':
          case 'setup':
            return true;
          case 'meals':
            return this.setupComplete;
          case 'review':
            // Everything but the meals count is knowable from the customer's own selections alone.
            if (!this.setupComplete) return false;
            // `assumeLimits` is for boot only. Until Recharge answers, `mealsRequired` is 0 — which means "not
            // known yet", not "not allowed". Reading it as a refusal sent a refresh on step 3 back to step 2,
            // where it stuck, because `clampStep` only ever moves backwards. A box with something in it is
            // allowed to stand on review until the limits can actually judge it; both steps show the same
            // loading gate meanwhile, so nothing of the wrong page is ever painted.
            if (options && options.assumeLimits && !this.dataReady) return this.mealsCount > 0;
            return this.mealsRequired > 0 && this.mealsCount === this.mealsRequired;
          default:
            return false;
        }
      },

      /** Furthest step at or before `wanted` that the customer is actually allowed to be on. */
      clampStep: function (wanted, options) {
        var steps = this.visibleSteps;
        var target = steps.indexOf(wanted) === -1 ? steps[0] : wanted;
        for (var i = steps.indexOf(target); i >= 0; i -= 1) {
          if (this.canEnter(steps[i], options)) return steps[i];
        }
        return steps[0];
      },

      goTo: function (step, options) {
        if (!this.canEnter(step)) return;
        if (this.step === step) return;
        // The customer has chosen for themselves; a later data load must not pull them back to whatever the
        // page was restored on.
        this.requestedStep = null;
        this.step = step;
        this.closePanel();
        this.closeBoxSheet();
        if (!options || options.push !== false) this.pushUrl();
        this.scrollToTop();
      },

      next: function () {
        var steps = this.visibleSteps;
        var target = steps[steps.indexOf(this.step) + 1];
        if (target) this.goTo(target);
      },

      back: function () {
        // Prefer real history so the browser's own back button and this control stay in sync — but only once WE
        // have pushed an entry. On load we replaceState, so history.state.bbStep is already set; delegating to
        // history.back() then would walk the customer off the builder entirely.
        if (this.pushedStates > 0) {
          window.history.back();
          return;
        }
        var steps = this.visibleSteps;
        var target = steps[steps.indexOf(this.step) - 1];
        if (target) this.goTo(target, { push: false });
      },

      get canContinue() {
        if (this.isBlockedOnData) return false;
        switch (this.step) {
          case 'quiz':
            return true;
          case 'setup':
            // The meals count comes from Recharge, so hold Continue until the rules are in — the CTA names it.
            return this.setupComplete && this.currentVariantAvailable && this.dataReady && this.mealsRequired > 0;
          case 'meals':
            return this.mealsRequired > 0 && this.mealsCount === this.mealsRequired;
          default:
            return false;
        }
      },

      /** Plain-language reason the Continue button is off (spec §11). Never just grey a button out. */
      get continueBlockedReason() {
        if (this.isBlockedOnData) return '';
        if (this.step === 'review') return this.checkoutBlockedReason;
        if (this.canContinue) return '';

        if (this.step === 'meals') return this.mealsCountReason;
        if (this.step !== 'setup') return '';

        // Setup page: name the first unanswered section, in the order they are revealed.
        if (this.boxSize && !this.currentVariantAvailable) return this.str('sizeUnavailable');

        var sections = this.setupSections;
        for (var i = 0; i < sections.length; i += 1) {
          if (this.isSectionComplete(sections[i])) continue;
          switch (sections[i]) {
            case 'type':
              return this.str('chooseTypeFirst');
            case 'size':
              return this.str('chooseSizeFirst');
            case 'frequency':
              return this.str('chooseFrequencyFirst');
            case 'gift':
              return this.str('chooseGiftFirst');
          }
        }

        // Everything the customer can answer IS answered, so what remains is on our side. Never fall through to
        // an empty string here: that renders a dead button with no explanation, which is what a stuck Continue
        // looked like in testing.
        if (this.dataState === 'loading' || this.dataState === 'idle') return this.str('stillLoading');
        if (this.dataState === 'error') return this.dataErrorMessage || '';
        if (!this.mealsLimits) return this.str('limitsUnknown');
        return '';
      },

      get mealsCountReason() {
        if (!this.mealsRequired) return this.mealsLimits ? '' : this.str('limitsUnknown');
        if (this.mealsCount === this.mealsRequired) return '';
        if (this.mealsCount < this.mealsRequired) {
          return interpolate(config.strings.addMoreMeals, { amount: this.mealsRequired - this.mealsCount });
        }
        return interpolate(config.strings.removeMeals, { amount: this.mealsCount - this.mealsRequired });
      },

      /** Why checkout is blocked, in the order a customer would most usefully hear it. */
      get checkoutBlockedReason() {
        var errors = this.validationErrors;
        if (!errors.length) return '';
        if (errors.indexOf('purchase_type') !== -1) return this.str('chooseTypeFirst');
        if (errors.indexOf('box_size') !== -1) return this.str('chooseSizeFirst');
        if (errors.indexOf('variant_unavailable') !== -1) return this.str('sizeUnavailable');
        if (errors.indexOf('selling_plan_required') !== -1) return this.str('chooseFrequencyFirst');
        if (errors.indexOf('gift_required') !== -1) return this.str('chooseGiftFirst');
        if (errors.indexOf('meals_count') !== -1 && this.mealsRequired) return this.mealsCountReason;
        if (errors.indexOf('bundle_limits_unknown') !== -1) return this.str('limitsUnknown');
        // Anything left is a configuration problem the customer cannot fix — stay generic and non-alarming.
        return this.str('fixBeforeCheckout');
      },

      /* `instant` where the page has just changed under the customer — history, a return from checkout — and a
         glide from the old depth would only show them a page scrolling past that they never asked to see. */
      scrollToTop: function (options) {
        var root = document.querySelector('[data-bundle-builder]');
        if (!root) return;
        var top = root.getBoundingClientRect().top + window.scrollY - 24;
        window.scrollTo({ top: top < 0 ? 0 : top, behavior: options && options.instant ? 'auto' : 'smooth' });
      },

      /* --- URL ------------------------------------------------------------------------------------------ */

      readStepFromUrl: function () {
        return normaliseStep(new URLSearchParams(window.location.search).get('step'));
      },

      stepUrl: function (step) {
        return window.location.pathname + '?step=' + step;
      },

      pushUrl: function () {
        window.history.pushState({ bbStep: this.step }, '', this.stepUrl(this.step));
        this.pushedStates += 1;
      },

      replaceUrl: function () {
        window.history.replaceState({ bbStep: this.step }, '', this.stepUrl(this.step));
      },

      /* Returns whether the link named a box.

         Entry points (spec §6), all whitelisted, so a mangled ad URL degrades to the normal flow rather than
         throwing or blank-screening. */
      applyUrlParams: function () {
        var params = new URLSearchParams(window.location.search);

        if (params.get('skip_quiz') === '1') this.quiz.skipped = true;

        /* A link that names a box — a homepage offer, an ad — is this visit's word on it, so it replaces the box
           saved from an earlier visit rather than being merged into it: the 6-meal one-time offer opened on a
           saved 8 (client feedback, 2026-10-06). Everything the box holds goes, not just the two choices the link
           names, since a saved menu or gift belongs to the old box. The quiz stays, and so does `cartLink`: if
           that old box is in the cart, checking out this one still has to take it out. */
        var namesABox = BOX_URL_PARAMS.some(function (name) {
          return params.get(name);
        });
        if (namesABox) this.discardBox();

        var purchaseType = params.get('purchase_type');
        if (purchaseType === 'onetime' || purchaseType === 'subscription') this.setPurchaseType(purchaseType);

        var boxSize = parseInt(params.get('box_size'), 10);
        if (this.boxSizeExists(boxSize)) this.setBoxSize(boxSize, { silent: true });

        // The spec's ad URLs use weekly/biweekly; map them onto whatever plan actually has that cadence.
        var frequency = params.get('frequency');
        if (frequency === 'weekly') this.selectPlanByIntervalKey('week-1');
        if (frequency === 'biweekly') this.selectPlanByIntervalKey('week-2');

        // Old per-step values (`type`, `size`, `frequency`, `gift`, `addons`) fold onto the page that now
        // contains them, so links already handed to ad platforms keep working.
        var step = normaliseStep(params.get('step'));
        if (step) {
          // Asking for the quiz by URL is asking to TAKE it, so it clears whatever had taken it out of the
          // flow. Runs after `skip_quiz` above on purpose: the explicit step is the later, stronger word.
          if (step === 'quiz') this.requestQuiz();
          this.requestedStep = step;
          this.step = step;
        }

        return namesABox;
      },

      /* --- Selections --- */

      /** Back to an empty box on the first step. */
      discardBox: function () {
        this.purchaseType = null;
        this.boxSize = null;
        this.sellingPlanId = null;
        this.gift = null;
        this.meals = [];
        this.addons = [];
        this.requestedStep = null;
        this.step = 'setup';
        this.persist();
      },

      setPurchaseType: function (value) {
        if (value !== 'onetime' && value !== 'subscription') return;
        if (this.purchaseType === value) return;

        var previousSize = this.boxSize;
        this.purchaseType = value;

        // The two bundles are different products, so the variant has to be re-resolved. Keep the customer's
        // chosen size if the other bundle offers it too; otherwise clear it and let them pick again.
        this.boxSize = this.boxSizeExists(previousSize) ? previousSize : null;

        if (value === 'onetime') {
          // The one-time product has no subscription plans and no gift collection.
          this.sellingPlanId = null;
          this.gift = null;
          this.addons.forEach(function (addon) {
            addon.mode = 'onetime';
          });
        } else {
          this.autoSelectPlanIfObvious();
        }

        this.ensureBundleData(value);
        this.persist();
      },

      boxSizeExists: function (size) {
        var value = parseInt(size, 10);
        if (!value) return false;
        var bundle = this.activeBundle;
        if (!bundle) return false;
        return bundle.variants.some(function (variant) {
          return variant.boxSize === value;
        });
      },

      /** Box sizes offered by the ACTIVE bundle product, smallest first. Never a hardcoded list. Before a type
          is chosen, the subscription's, shown greyed (see `displayType`). */
      get sizeOptions() {
        var bundle = this.displayBundle;
        if (!bundle) return [];
        return bundle.variants
          .filter(function (variant) {
            return variant.boxSize > 0;
          })
          .slice()
          .sort(function (a, b) {
            return a.boxSize - b.boxSize;
          });
      },

      setBoxSize: function (size, options) {
        var value = parseInt(size, 10);
        if (!this.boxSizeExists(value)) return;
        if (this.boxSize === value) return;

        var previous = this.boxSize;
        this.boxSize = value;

        // Plans live on the variant, so a size change can change which plans exist.
        if (this.purchaseType === 'subscription') {
          var currentPlanId = this.sellingPlanId;
          var stillOffered = this.availablePlans.some(function (plan) {
            return plan.id === currentPlanId;
          });
          if (!stillOffered) {
            this.sellingPlanId = null;
            this.autoSelectPlanIfObvious();
          }
        }

        if (previous != null && !(options && options.silent)) this.reconcileMeals();
        this.persist();
      },

      get currentVariant() {
        return this.variantForSize(this.boxSize);
      },

      get currentVariantAvailable() {
        var variant = this.currentVariant;
        return Boolean(variant && variant.available);
      },

      variantForSize: function (size) {
        var bundle = this.activeBundle;
        if (!bundle || !size) return null;
        var value = parseInt(size, 10);
        return (
          bundle.variants.find(function (variant) {
            return variant.boxSize === value;
          }) || null
        );
      },

      /* --- Selling plans -------------------------------------------------------------------------------- */

      /* The plans the CHOSEN VARIANT actually offers, read from Shopify, so adding one in Recharge makes it
         appear here with no deploy.

         Always read off the subscription bundle — the one-time product carries no selling plans, and a one-time
         box shows the cadences greyed rather than hiding them. The frequency section is on screen before a size
         is picked, so until then the smallest size stands in; `setBoxSize` re-checks the choice against the
         real variant's plans once there is one. */
      get availablePlans() {
        var self = this;
        var subscription = this.bundleFor('subscription');
        if (!subscription) return [];

        var sizes = (subscription.variants || []).filter(function (candidate) {
          return candidate.boxSize > 0;
        });
        var size = this.boxSize;
        var variant = size
          ? sizes.find(function (candidate) {
              return candidate.boxSize === size;
            })
          : sizes.slice().sort(function (a, b) {
              return a.boxSize - b.boxSize;
            })[0];
        if (!variant) return [];

        return (variant.sellingPlans || [])
          .filter(function (plan) {
            return plan.recurring !== false;
          })
          .map(function (plan) {
            var interval = parseInterval(plan);
            return {
              id: plan.id,
              name: plan.name,
              price: plan.price,
              interval: interval,
              key: intervalKey(interval),
              label: self.planLabel(plan, interval),
              recurrenceLabel: self.planRecurrenceLabel(plan, interval),
              chargeNote: self.planChargeNote(plan, interval),
              chargeNoteShort: self.planChargeNote(plan, interval, true),
              subscriptionLabel: self.planSubscriptionLabel(plan, interval)
            };
          })
          .sort(function (a, b) {
            return (a.interval ? a.interval.count : 99) - (b.interval ? b.interval.count : 99);
          });
      },

      /* Built from the parsed cadence so the copy stays in the translation files; falls back to the plan's own
         Shopify name if the cadence could not be read. */
      planLabel: function (plan, interval) {
        if (!interval) return plan.name || '';
        var key = interval.unit + (interval.count === 1 ? '_1' : '_n');
        var template = config.strings.intervals[key];
        if (!template) return plan.name || '';
        return interpolate(template, { count: interval.count });
      },

      /** "Charged every week until you pause or cancel" — the cadence in billing terms. `short` is the
          closing line's "Charged weekly until paused". */
      planChargeNote: function (plan, interval, short) {
        if (!interval) return '';
        var key = (short ? 'charged_short_' : 'charged_') + interval.unit + (interval.count === 1 ? '_1' : '_n');
        var template = config.strings.intervals[key];
        if (!template) return '';
        return interpolate(template, { count: interval.count });
      },

      /** "weekly subscription" / "subscription every 2 weeks" — the plan as the review page names it. */
      planSubscriptionLabel: function (plan, interval) {
        if (!interval) return '';
        var key = 'subscription_' + interval.unit + (interval.count === 1 ? '_1' : '_n');
        var template = config.strings.intervals[key];
        if (!template) return '';
        return interpolate(template, { count: interval.count });
      },

      /** "then every week" / "then every 2 weeks", for the delivery note. */
      planRecurrenceLabel: function (plan, interval) {
        if (!interval) return '';
        var key = 'then_' + interval.unit + (interval.count === 1 ? '_1' : '_n');
        var template = config.strings.intervals[key];
        if (!template) return '';
        return interpolate(template, { count: interval.count });
      },

      get selectedPlan() {
        var id = this.sellingPlanId;
        // A one-time box has no plan even though `availablePlans` lists the subscription's, for display.
        if (!id || this.purchaseType !== 'subscription') return null;
        return (
          this.availablePlans.find(function (plan) {
            return plan.id === id;
          }) || null
        );
      },

      selectPlan: function (planId) {
        // The cadences are listed on a one-time box only to be greyed out; attaching one would put a selling
        // plan on a product that has none.
        if (this.purchaseType !== 'subscription') return;

        var id = Number(planId);
        var exists = this.availablePlans.some(function (plan) {
          return plan.id === id;
        });
        if (!exists) return;

        var changed = this.sellingPlanId !== null && this.sellingPlanId !== id;
        this.sellingPlanId = id;

        // Recharge can hold a different charge day per plan, so the date has to be recomputed on a cadence change
        // rather than only on the clock tick.
        this.recomputeDeliveryDate('plan changed');

        // A recurring add-on must charge on the same cadence as the bundle (spec §9). The add-on's plan is
        // re-resolved from its own interval at add-to-cart, so remapping is automatic — but say so.
        var hasRecurringAddons = this.addons.some(function (addon) {
          return addon.mode === 'subscription';
        });
        if (changed && hasRecurringAddons) {
          var plan = this.selectedPlan;
        }

        this.persist();
      },

      selectPlanByIntervalKey: function (key) {
        var match = this.availablePlans.find(function (plan) {
          return plan.key === key;
        });
        if (match) this.selectPlan(match.id);
      },

      /** With a single cadence on offer there is nothing to decide — preselect it and let them just continue. */
      autoSelectPlanIfObvious: function () {
        if (this.sellingPlanId) return;
        var plans = this.availablePlans;
        if (plans.length === 1) this.sellingPlanId = plans[0].id;
      },

      get frequencyLabel() {
        var plan = this.selectedPlan;
        return plan ? plan.recurrenceLabel : '';
      },

      /* --- One-line summaries, used by the mobile strip and the review header ---------------------------- */

      /** "6 meals · subscription · every week" — only the parts that are actually known. */
      get setupSummaryLine() {
        var parts = [];

        if (this.mealsRequired > 0) {
          parts.push(interpolate(config.strings.summaryMeals, { count: this.mealsRequired }));
        } else if (this.boxSize) {
          parts.push(interpolate(config.strings.summaryMeals, { count: this.boxSize }));
        }

        if (this.purchaseType) {
          parts.push(
            this.purchaseType === 'subscription' ? config.strings.typeSubscription : config.strings.typeOnetime
          );
        }

        var plan = this.selectedPlan;
        if (plan && plan.label) parts.push(plan.label.toLowerCase());

        return parts.join(' · ');
      },

      /** The reassurance line under the review header: delivery, order-by day, savings, gift, cancellation. */
      get deliveryDetailLine() {
        var parts = [];
        if (this.deliveryDateLabel) {
          parts.push(interpolate(config.strings.nextDeliveryOn, { date: this.deliveryDateLabel }));
        }
        parts.push(config.strings.orderByShort);
        if (this.discountPercent) parts.push(interpolate(config.strings.percentOffApplied, { percent: this.discountPercent }));
        if (this.gift) parts.push(interpolate(config.strings.freeGiftNamed, { title: this.gift.title }));
        if (this.purchaseType === 'subscription') parts.push(config.strings.cancelAnyTime);
        return parts.join(' · ');
      },

      /** "Pick 6 meals — 2 to go", collapsing to a plain heading once the box is full. */
      get mealsHeading() {
        if (!this.mealsRequired) return config.strings.mealsHeadingPlain;
        // The same sentence the blocked CTA uses, deliberately: two different phrasings of "remove 2" sitting
        // a few hundred pixels apart read as two different instructions.
        if (this.mealsOver > 0) {
          return interpolate(config.strings.removeMeals, { amount: this.mealsOver });
        }
        if (this.mealsRemaining > 0) {
          return interpolate(config.strings.mealsHeading, {
            total: this.mealsRequired,
            remaining: this.mealsRemaining
          });
        }
        return interpolate(config.strings.mealsHeadingFull, { total: this.mealsRequired });
      },

      /** "2 remaining" for the mobile bar, blank once the box is full. */
      get remainingLabel() {
        if (!this.mealsRequired) return '';
        if (this.mealsOver > 0) return interpolate(config.strings.overBy, { count: this.mealsOver });
        if (this.mealsRemaining === 0) return '';
        return interpolate(config.strings.remaining, { count: this.mealsRemaining });
      },

      /** The bar's closing line — "Delivers Tue 8 Sep · Charged weekly until paused", word for word the frames'
          (2197:16767, 2269:86), short enough to stay on one row. The date leads and the money follows, which is
          the order both phone frames draw (2197:16765 closed, 2197:16763 open) and the answer someone scanning
          the bar is after. The next-delivery plate above it keeps the full "until you pause or cancel". */
      /* The bar's small print comes in two parts so the delivery date can stand out from the charge terms after
         it (client feedback, 2026-10-12). `chargeNote` is the two joined, for anything that wants the whole line. */
      get chargeNoteDate() {
        if (!this.deliveryDateShortLabel) return '';
        return interpolate(config.strings.deliversOn, { date: this.deliveryDateShortLabel });
      },

      get chargeNoteTerms() {
        var plan = this.selectedPlan;
        if (plan && plan.chargeNoteShort) return plan.chargeNoteShort;
        if (this.purchaseType === 'onetime') return config.strings.oneTimeCharge;
        return '';
      },

      get chargeNote() {
        return [this.chargeNoteDate, this.chargeNoteTerms].filter(Boolean).join(' · ');
      },

      /* --- Gift ----------------------------------------------------------------------------------------- */

      selectGift: function (productId, variantId, collectionId, title) {
        if (this.purchaseType !== 'subscription') return;
        this.gift = {
          productId: String(productId),
          variantId: Number(variantId),
          collectionId: String(collectionId),
          title: title
        };
        this.persist();
      },

      /* Card lookups. The rail and review lines show a thumbnail and a dietary line per chosen dish. Neither is
         persisted: both are read back off the card Liquid already rendered. A merchant who swaps a photo or
         retags a dish sees it on the next load, instead of it being frozen into a customer's localStorage. */

      cardFor: function (variantId) {
        return document.querySelector('[data-bb-card][data-variant-id="' + Number(variantId) + '"]');
      },

      imageFor: function (variantId) {
        var card = this.cardFor(variantId);
        return card && card.dataset.image ? card.dataset.image : '';
      },

      /** "HIGH PROTEIN · GLUTEN FREE" — the review line's sub-label. Empty when the dish carries no tags. */
      dietLineFor: function (variantId) {
        var card = this.cardFor(variantId);
        if (!card || !card.dataset.diets) return '';
        var labels = config.strings.diets || {};
        return card.dataset.diets
          .split(',')
          .filter(Boolean)
          .map(function (key) {
            return labels[key] || key;
          })
          .join(' · ');
      },

      /* --- Meals and add-ons ---------------------------------------------------------------------------- */

      listFor: function (kind) {
        return kind === 'addon' ? this.addons : this.meals;
      },

      quantityOf: function (kind, variantId) {
        var id = Number(variantId);
        // The gift is a single object rather than a list, but the card markup is shared, so answer in the same
        // shape: 1 when this is the chosen gift, 0 otherwise.
        if (kind === 'gift') return this.gift && this.gift.variantId === id ? 1 : 0;
        var entry = this.listFor(kind).find(function (item) {
          return item.variantId === id;
        });
        return entry ? entry.quantity : 0;
      },

      increment: function (kind, productId, variantId, collectionId, title) {
        if (kind === 'meal' && (!this.mealsMax || this.mealsCount >= this.mealsMax)) return;

        var list = this.listFor(kind);
        var id = Number(variantId);
        var entry = list.find(function (item) {
          return item.variantId === id;
        });

        if (entry) {
          entry.quantity += 1;
        } else {
          var record = {
            productId: String(productId),
            variantId: id,
            collectionId: String(collectionId || ''),
            quantity: 1,
            title: title
          };
          // Add-ons carry their own mode; recurring is only offered on a subscription bundle (spec §9).
          if (kind === 'addon') record.mode = 'onetime';
          list.push(record);
        }

        this.persist();
      },

      decrement: function (kind, variantId) {
        var list = this.listFor(kind);
        var id = Number(variantId);
        var index = list.findIndex(function (item) {
          return item.variantId === id;
        });
        if (index === -1) return;

        list[index].quantity -= 1;
        if (list[index].quantity <= 0) {
          list.splice(index, 1);
        }
        this.persist();
      },

      isAddDisabled: function (kind) {
        if (kind !== 'meal') return false; // add-ons have no min and no max (spec §5.6)
        return !this.mealsMax || this.mealsCount >= this.mealsMax;
      },

      /* --- Add-on modes --------------------------------------------------------------------------------- */

      addonMode: function (variantId) {
        var id = Number(variantId);
        var entry = this.addons.find(function (item) {
          return item.variantId === id;
        });
        return entry ? entry.mode : 'onetime';
      },

      /* Matched by parsed interval, not by name text, so the two products can be named independently. */
      addonPlanFor: function (variantId) {
        var bundlePlan = this.selectedPlan;
        if (!bundlePlan || !bundlePlan.key) return null;

        var addon = (config.addons || {})[String(variantId)];
        if (!addon) return null;

        var match = (addon.sellingPlans || []).find(function (plan) {
          if (plan.recurring === false) return false;
          return intervalKey(parseInterval(plan)) === bundlePlan.key;
        });
        return match || null;
      },

      addonSupportsRecurring: function (variantId) {
        return this.purchaseType === 'subscription' && Boolean(this.addonPlanFor(variantId));
      },

      /* Two buttons rather than an add plus a toggle, so the commitment is chosen in the same click as the add.
         Order matters: setAddonMode only writes to an entry that already exists. */
      addAddon: function (productId, variantId, collectionId, title, mode) {
        this.increment('addon', productId, variantId, collectionId, title);
        if (mode === 'subscription') this.setAddonMode(variantId, 'subscription');
      },

      setAddonMode: function (variantId, mode) {
        if (mode === 'subscription' && !this.addonSupportsRecurring(variantId)) {
          return;
        }
        var id = Number(variantId);
        var entry = this.addons.find(function (item) {
          return item.variantId === id;
        });
        if (!entry) return;
        entry.mode = mode;
        this.persist();
      },

      /* --- Box limits ----------------------------------------------------------------------------------- */

      /* Reads the MEALS collection limits, never `items_count`: on the subscription bundle a "4 Meals" variant
         has items_count = 5 because one slot is the gift, and "0 of 5 meals" would be wrong. Returns null when
         the answer cannot be established, so the caller blocks rather than guessing. */
      get mealsLimits() {
        var variant = this.currentVariant;
        var entry = this.dataEntry;
        if (!variant || !entry || entry.state !== 'ready' || !entry.byVariantId) return null;

        var settings = entry.byVariantId[String(variant.id)];
        if (!settings) return null;

        var mealsCollectionId = String(config.recharge.collections.meals);
        var binding = settings.collections.find(function (item) {
          return item.id === mealsCollectionId;
        });

        // 1. An explicit per-collection limit always wins.
        if (binding && (binding.min != null || binding.max != null)) {
          var min = binding.min != null && binding.min > 0 ? binding.min : binding.max;
          var max = binding.max != null && binding.max > 0 ? binding.max : binding.min;
          if (min) return { min: min, max: max || min };
        }

        // 2. A single-source bundle (the one-time product) has no gift slot, so every item is a meal.
        if (settings.collections.length <= 1 && settings.itemsCount) {
          return { min: settings.itemsCount, max: settings.itemsCount };
        }

        // 3. Several sources and no per-collection limits — we cannot tell meals from gifts. Do not guess.
        return null;
      },

      get mealsRequired() {
        var limits = this.mealsLimits;
        return limits ? limits.min : 0;
      },

      get mealsMax() {
        var limits = this.mealsLimits;
        return limits ? limits.max : 0;
      },

      get mealsCount() {
        return this.meals.reduce(function (total, item) {
          return total + item.quantity;
        }, 0);
      },

      get mealsRemaining() {
        return Math.max(0, this.mealsRequired - this.mealsCount);
      },

      /** How many meals OVER the box size — where shrinking a filled box now leaves the customer. */
      get mealsOver() {
        if (!this.mealsRequired) return 0;
        return Math.max(0, this.mealsCount - this.mealsRequired);
      },

      get mealsProgressPercent() {
        if (!this.mealsRequired) return 0;
        return Math.min(100, Math.round((this.mealsCount / this.mealsRequired) * 100));
      },

      get progressLabel() {
        return interpolate(config.strings.mealsSelected, {
          selected: this.mealsCount,
          total: this.mealsRequired
        });
      },

      /* Box size changed mid-flow (spec §5). Only meals the NEW variant cannot source are dropped — a dish it
         has no collection for genuinely cannot be ordered.

         A smaller box is deliberately NOT trimmed: deciding which two of eight dishes to lose is the customer's
         call. The counts read "8 of 6" and "remove 2", + is capped, and Continue stays blocked, so the only way
         out is down. */
      reconcileMeals: function () {
        var variant = this.currentVariant;
        var entry = this.dataEntry;
        var settings = variant && entry && entry.byVariantId ? entry.byVariantId[String(variant.id)] : null;
        if (!settings || !settings.collections.length) return;

        var allowed = settings.collections.map(function (binding) {
          return binding.id;
        });
        this.meals = this.meals.filter(function (meal) {
          return !meal.collectionId || allowed.indexOf(meal.collectionId) !== -1;
        });
      },

      /* --- Filters (client-side, over Liquid-rendered cards) -------------------------------------------- */

      indexFilterCounts: function () {
        var counts = {};
        document.querySelectorAll('[data-bb-card][data-kind="meal"]').forEach(function (card) {
          (card.dataset.diets || '')
            .split(',')
            .filter(Boolean)
            .forEach(function (diet) {
              counts[diet] = (counts[diet] || 0) + 1;
            });
        });
        this.filterCounts = counts;
      },

      seedVariantChoices: function () {
        var self = this;
        document.querySelectorAll('[data-bb-card]').forEach(function (card) {
          var productId = card.dataset.productId;
          var variantId = Number(card.dataset.variantId);
          if (productId && variantId && !self.variantChoice[productId]) {
            self.variantChoice[productId] = variantId;
          }
        });
      },

      toggleFilter: function (diet) {
        var index = this.activeFilters.indexOf(diet);
        if (index === -1) this.activeFilters.push(diet);
        else this.activeFilters.splice(index, 1);
        // Changing the filter changes which cards are eligible, so re-index and start the reveal over.
        this.mealsShown = config.grid.mealsPerLoad;
        this.reindexGrids();
        // A shorter grid can leave its end in reach without the observer seeing a change.
        this.checkGridEndSoon('meal');
      },

      clearFilters: function () {
        this.activeFilters = [];
        this.mealsShown = config.grid.mealsPerLoad;
        this.reindexGrids();
        this.checkGridEndSoon('meal');
      },

      isFilterActive: function (diet) {
        return this.activeFilters.indexOf(diet) !== -1;
      },

      /* A card's reveal position is its index among the cards that PASS THE CURRENT FILTER, which Liquid cannot
         know at render time. Re-indexed eagerly on every filter change, not inside a getter — mutating store
         state during a getter would loop Alpine's reactivity. */

      reindexGrids: function () {
        var self = this;

        var mealOrder = {};
        var matching = 0;
        document.querySelectorAll('[data-bb-card][data-kind="meal"]').forEach(function (card) {
          var diets = (card.dataset.diets || '').split(',').filter(Boolean);
          if (!self.isCardVisible('meal', diets)) return;
          mealOrder[card.dataset.variantId] = matching;
          matching += 1;
        });
        this.mealOrder = mealOrder;
        this.mealMatchCount = matching;

        var addonOrder = {};
        var addonIndex = 0;
        document.querySelectorAll('[data-bb-card][data-kind="addon"]').forEach(function (card) {
          addonOrder[card.dataset.variantId] = addonIndex;
          addonIndex += 1;
        });
        this.addonOrder = addonOrder;
        this.addonCount = addonIndex;
      },

      /* An id missing from the index — a card added since the last reindex — errs toward showing it. */
      isCardShown: function (kind, diets, variantId) {
        if (kind === 'gift') return true;

        if (kind === 'addon') {
          var addonIndex = this.addonOrder[variantId];
          return addonIndex === undefined ? true : addonIndex < this.addonsShown;
        }

        if (!this.isCardVisible('meal', diets)) return false;
        var mealIndex = this.mealOrder[variantId];
        return mealIndex === undefined ? true : mealIndex < this.mealsShown;
      },

      get hasMoreMeals() {
        return this.mealMatchCount > this.mealsShown;
      },

      loadMoreMeals: function () {
        this.mealsShown += config.grid.mealsPerLoad;
      },

      get hasMoreAddons() {
        return this.addonCount > this.addonsShown;
      },

      loadMoreAddons: function () {
        this.addonsShown += config.grid.addonsPerLoad;
      },

      /* The meal and add-on grids load their next batch on their own as the customer scrolls towards the end;
         there is no Load More button any more (client feedback, 2026-10-08). `marker` is the empty element under
         the grid, shown only while more cards are waiting, so it appears and vanishes with them, and with its
         page. */
      watchGridEnd: function (marker, kind) {
        var self = this;
        new IntersectionObserver(
          function (entries) {
            if (entries[entries.length - 1].isIntersecting) self.checkGridEnd(kind);
          },
          { rootMargin: '0px 0px ' + GRID_LOAD_AHEAD_PX + 'px 0px' }
        ).observe(marker);
      },

      /* Measures rather than trusting the observer, which only reports a change. A batch too short to carry the
         marker back out of reach (a tall window, a narrow filter) would leave it in reach and nothing would ever
         fire again, so each batch looks once more after it is laid out. A filter change, which can shorten the
         grid under a marker already in reach, asks the same question.

         It measures the grid, not the marker: straight after a filter change the marker can still be hidden,
         waiting on the same frame as the cards (see `checkGridEndSoon`). */
      checkGridEnd: function (kind) {
        if (gridRevealPending[kind]) return;
        if (kind === 'addon' ? !this.hasMoreAddons : !this.hasMoreMeals) return;

        var marker = document.querySelector('[data-bb-grid-end="' + kind + '"]');
        var grid = marker && marker.parentElement.querySelector('.bb-grid');
        // No boxes: the grid's page is `display: none`.
        if (!grid || !grid.getClientRects().length) return;
        if (grid.getBoundingClientRect().bottom > window.innerHeight + GRID_LOAD_AHEAD_PX) return;

        if (kind === 'addon') this.loadMoreAddons();
        else this.loadMoreMeals();
        this.checkGridEndSoon(kind);
      },

      /* Alpine's `x-show` reveals an element on the next animation frame, not at once, so cards just shown or
         hidden can only be measured after one. Until then the grid is still the old height and would ask for
         batch after batch, so the observer is held off too. */
      checkGridEndSoon: function (kind) {
        var self = this;
        gridRevealPending[kind] = true;
        window.Alpine.nextTick(function () {
          requestAnimationFrame(function () {
            gridRevealPending[kind] = false;
            self.checkGridEnd(kind);
          });
        });
      },

      /** OR within the single filter group — selecting two categories widens the list rather than emptying it. */
      isCardVisible: function (kind, diets) {
        if (kind !== 'meal') return true;
        if (!this.activeFilters.length) return true;
        var list = diets || [];
        return this.activeFilters.some(function (diet) {
          return list.indexOf(diet) !== -1;
        });
      },

      get visibleMealCount() {
        return this.mealMatchCount;
      },

      /* The desktop rail is display:none under 1000px, so "View box" in the sticky bar opens the same content. */

      /* Mobile bar. The rail's job on a small screen: where am I, how far along, and what happens next. */

      /** "· Step 1 of 3" — the setup page has no meal count to show yet. */
      get stepOfTotalLabel() {
        return interpolate(this.str('stepOfTotal'), { step: this.stepNumber, total: this.stepCount });
      },

      /* How full the box is, always — an empty box reads empty wherever you are. A deliberate departure from
         Figma, which fills step 1's meter a third beside an empty box; awais settled it on 2026-09-26 that the
         bar represents meals and nothing else. Do not "correct" it back. */
      get barProgressPercent() {
        return this.mealsProgressPercent;
      },

      /* The status line switches with the meter, on the box and never on the step: anything in the box is
         counted, an empty box names the step. Back on setup with a part-filled box still counts. */
      get barShowsMeals() {
        return this.mealsRequired > 0 && this.mealsCount > 0;
      },

      /** The page the bar's button leads to. Null on the review page, which checks out instead. */
      get barNextStep() {
        if (this.step === 'setup') return 'meals';
        if (this.step === 'meals') return 'review';
        return null;
      },

      /* The box sheet (2746:36). Its heading is the box's count in words — "6 of 6 meals" — where the bar beside
         an empty box names the step instead; that label starts "· Step", which only reads beside "YOUR BOX". */
      get sheetHeadline() {
        if (!this.mealsRequired) return this.str('noSizeYet');
        return this.progressLabel;
      },

      /** Exactly full: the sheet's "Box complete" line. Over the size is not complete, it is too many. */
      get isBoxComplete() {
        return this.mealsRequired > 0 && this.mealsCount === this.mealsRequired;
      },

      /** "6 meals · weekly subscription" — the sheet's plan card (2746:130). */
      get sheetPlanTitle() {
        var parts = [];
        var count = this.mealsRequired || this.boxSize;
        if (count) parts.push(interpolate(config.strings.summaryMeals, { count: count }));

        var plan = this.selectedPlan;
        var planName = plan ? plan.subscriptionLabel || (plan.label && plan.label.toLowerCase()) : '';
        if (this.purchaseType === 'subscription' && planName) parts.push(planName);
        else if (this.purchaseType === 'onetime') parts.push(config.strings.typeOnetime);

        return parts.filter(Boolean).join(' · ');
      },

      /* The sheet's "Edit" links. The page they lead to may be the one already under the sheet, where `goTo`
         does nothing, so there they just close it. */
      goToFromSheet: function (step) {
        if (this.step === step) this.closeBoxSheet();
        else this.goTo(step);
      },

      /* A dialog now, over a scrim, so focus moves into it and comes back to "View box" when it shuts. It lands on
         the sheet itself rather than on "Close box", which would wear the theme's focus ring on every opening. */
      openBoxSheet: function () {
        this.boxSheetOpen = true;
        document.documentElement.style.overflow = 'hidden';
        window.Alpine.nextTick(function () {
          var sheet = document.querySelector('[data-bb-sheet]');
          if (sheet) sheet.focus({ preventScroll: true });
        });
      },

      /* A tap anywhere on the shut bar opens it, not only on "View box" (client feedback, 2026-10-12). A tap on
         one of the bar's own controls is left to that control: the CTA still continues, and the toggle's own
         close is not undone by this reopening it. Keyboard users keep the toggle, which is a real button. */
      openBoxSheetFromBar: function (event) {
        if (this.boxSheetOpen) return;
        var target = event && event.target;
        if (target && target.closest && target.closest('a, button, input, select, textarea, label, summary')) return;
        this.openBoxSheet();
      },

      closeBoxSheet: function () {
        if (!this.boxSheetOpen) return;
        var sheet = document.querySelector('[data-bb-sheet]');
        var hadFocus = Boolean(sheet && sheet.contains(document.activeElement));
        this.boxSheetOpen = false;
        document.documentElement.style.overflow = '';
        if (!hadFocus) return;
        // After Alpine has lifted the bar's `inert`, or the focus call is refused.
        window.Alpine.nextTick(function () {
          var toggle = document.querySelector('.bb-bar__toggle');
          if (toggle) toggle.focus({ preventScroll: true });
        });
      },

      /* --- Slide-out panel --- */

      openPanel: function (variantId, trigger, kind) {
        var body = document.querySelector('[data-bb-panel-body]');
        if (!body) return;

        /* Resolve the card from the button that was pressed, not from the variant id.
           A product can legitimately sit in two grids at once — a brownie that is both a free gift and a paid
           add-on is the obvious case — and matching on the id alone returned whichever card came first in the
           DOM. The gifts grid renders before the add-ons grid, so opening that add-on read `kind: 'gift'` and
           the footer drew the gift's single "Choose" button instead of the two commitments. */
        var card = trigger && trigger.closest ? trigger.closest('[data-bb-card]') : null;
        /* Opened without a trigger — a test, a deep link — the caller has to say which grid it meant, because
           the id alone cannot. Falling through to the first match is the old behaviour and the old bug, kept
           only so a caller that knows neither still gets a panel rather than nothing. */
        if (!card && kind) {
          card = document.querySelector('[data-bb-card][data-kind="' + kind + '"][data-variant-id="' + Number(variantId) + '"]');
        }
        if (!card) card = document.querySelector('[data-bb-card][data-variant-id="' + Number(variantId) + '"]');

        /* Same ambiguity for the detail markup, which Liquid renders immediately after its own card. Prefer
           that sibling, then anything in the same grid, and only then fall back to the whole document. */
        var selector = '[data-bb-panel-template="' + variantId + '"]';
        var template = null;
        if (card) {
          var next = card.nextElementSibling;
          if (next && next.matches && next.matches(selector)) template = next;
          if (!template && card.parentElement) template = card.parentElement.querySelector(selector);
        }
        if (!template) template = document.querySelector(selector);

        body.replaceChildren();
        if (template) {
          // Marked before it goes in, so anything translating the page in place sees the finished markup.
          var details = template.content.cloneNode(true);
          highlightAllergens(details, allergens);
          body.appendChild(details);
        }

        this.panel = {
          open: true,
          variantId: Number(variantId),
          title: card ? card.dataset.title : '',
          kind: card ? card.dataset.kind : 'meal',
          collectionId: card ? card.dataset.collectionId : '',
          productId: card ? card.dataset.productId : '',
          trigger: trigger || null
        };

        /* The stepper starts at one for something not yet in the box — that is the quantity the add buttons
           will commit, not a quantity the box already holds. */
        this.panelDraftQty = 1;

        document.documentElement.style.overflow = 'hidden';

        var self = this;
        requestAnimationFrame(function () {
          var panel = document.querySelector('[data-bb-panel]');
          if (!panel) return;
          var close = panel.querySelector('[data-bb-panel-close]');
          (close || panel).focus();
          self.trapFocus(panel);
        });
      },

      /** The panel's primary action. Adds one when the dish is not yet in the box; otherwise leaves it alone. */
      /* True when the panel is showing an add-on the customer could take on every delivery. The panel then
         offers both commitments as two buttons (2248:305), the way the add-on card does. */
      get panelOffersEveryBox() {
        return this.panel.kind === 'addon' && this.addonSupportsRecurring(this.panel.variantId);
      },

      /* Which commitment the panel's add-on is in the box under — 'onetime', 'subscription', or null when it is
         not in the box at all. The two footer buttons read this to decide which of them says "Added": without it
         they both read as an invitation to add, on an add-on the customer has already added. */
      /* True while the panel's item is already in the box, which is what decides whether its stepper is a
         draft or a live editor. */
      get panelItemInBox() {
        return Boolean(this.panel.variantId) && this.quantityOf(this.panel.kind, this.panel.variantId) > 0;
      },

      /* What the panel's stepper shows: the real quantity once it is in the box, the draft before that. It used
         to show `Math.max(1, quantity)`, which read "1" over an empty box — so the first press of + moved 0 to 1
         and the number did not change, and shoppers pressed it twice. */
      get panelQty() {
        return this.panelItemInBox ? this.quantityOf(this.panel.kind, this.panel.variantId) : this.panelDraftQty;
      },

      /* The draft counts against the box before anything is in it. It used to ask only whether the box was
         already full, so with 2 of 6 in it climbed to 15 — and "Add to box" then quietly stopped at 4. */
      get panelCanStepUp() {
        if (this.panelItemInBox) return !this.isAddDisabled(this.panel.kind);
        if (this.panel.kind !== 'meal') return true;
        return this.panelDraftQty < this.mealsMax - this.mealsCount;
      },

      panelStepUp: function () {
        if (!this.panelCanStepUp) return;
        if (this.panelItemInBox) {
          this.increment(this.panel.kind, this.panel.productId, this.panel.variantId, this.panel.collectionId, this.panel.title);
          return;
        }
        this.panelDraftQty += 1;
      },

      panelStepDown: function () {
        if (this.panelItemInBox) {
          this.decrement(this.panel.kind, this.panel.variantId);
          return;
        }
        this.panelDraftQty = Math.max(1, this.panelDraftQty - 1);
      },

      get panelAddonMode() {
        if (this.panel.kind !== 'addon' || !this.panel.variantId) return null;
        if (this.quantityOf('addon', this.panel.variantId) === 0) return null;
        return this.addonMode(this.panel.variantId);
      },

      /* `mode` is only meaningful for add-ons: 'onetime' or 'subscription'. Passing nothing keeps the old
         behaviour, which is what meals and the single-button add-ons still want. */
      addFromPanel: function (mode) {
        var panel = this.panel;
        if (!panel.variantId) return;

        if (this.quantityOf(panel.kind, panel.variantId) > 0) {
          /* Already in the box, so the two buttons change the commitment rather than adding a second one. */
          if (panel.kind === 'addon' && mode) this.setAddonMode(panel.variantId, mode);
          this.closePanel();
          return;
        }

        /* Whatever the stepper was showing is what goes in — adding one when the panel said three is the bug
           this exists to prevent. The meal limit is re-checked on every pass, so a draft larger than the space
           left fills the box and stops. */
        var wanted = Math.max(1, Number(this.panelDraftQty) || 1);
        for (var i = 0; i < wanted; i++) {
          if (this.isAddDisabled(panel.kind)) break;
          if (panel.kind === 'addon' && mode) {
            this.addAddon(panel.productId, panel.variantId, panel.collectionId, panel.title, mode);
          } else {
            this.increment(panel.kind, panel.productId, panel.variantId, panel.collectionId, panel.title);
          }
        }
        this.closePanel();
      },

      closePanel: function () {
        if (!this.panel.open) return;
        var trigger = this.panel.trigger;

        if (this.releaseFocusTrap) this.releaseFocusTrap();
        this.panel = {
          open: false,
          variantId: null,
          title: '',
          kind: 'meal',
          collectionId: '',
          productId: '',
          trigger: null
        };
        document.documentElement.style.overflow = '';

        // Focus returns to the card's info button (spec §11).
        if (trigger && typeof trigger.focus === 'function') trigger.focus();
      },

      trapFocus: function (container) {
        var self = this;
        if (this.releaseFocusTrap) this.releaseFocusTrap();

        function onKeydown(event) {
          if (event.key !== 'Tab') return;
          var focusable = container.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, summary, [tabindex]:not([tabindex="-1"])'
          );
          if (!focusable.length) return;
          var first = focusable[0];
          var last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }

        container.addEventListener('keydown', onKeydown);
        this.releaseFocusTrap = function () {
          container.removeEventListener('keydown', onKeydown);
          self.releaseFocusTrap = null;
        };
      },

      /* --- Quiz --- */

      /* Deliberately does not advance: only the explicit Next button navigates. */
      answerQuiz: function (question, value) {
        this.quiz[question] = value;
        this.persist();
      },

      quizNext: function () {
        if (this.quizPage >= QUIZ_LAST_PAGE) return;
        this.quizPage += 1;
        this.persist();
      },

      quizBack: function () {
        if (this.quizPage <= 0) return;
        this.quizPage -= 1;
        this.persist();
      },

      /* Figma rules the track at 33 / 66 / 100 rather than exact thirds, so the marks are listed rather than
         divided — a third would sit 2.5px short of the drawing. */
      get quizProgress() {
        var marks = [33, 66, 100];
        return marks[Math.min(Math.max(this.quizPage, 0), marks.length - 1)];
      },

      /* Put the quiz back in the flow. One code path for "I want to take it", so the button and a `?step=quiz`
         link cannot behave differently — which they did: the button reaches `goTo`, whose `canEnter('quiz')` is
         unconditionally true, while a URL is clamped at boot against `visibleSteps`, which drops the quiz for
         anyone who has already answered or skipped it. */
      requestQuiz: function () {
        this.quizRequested = true;
        this.quiz.skipped = false;
        this.quiz.completedAt = null;
        this.quiz.klaviyoSent = false;
        this.quizPage = 0;
        this.prefillQuiz();
      },

      /* A signed-in customer retaking the quiz starts from the answers on their customer record, so the retake is
         three clicks when nothing has changed. Filled here, not at boot: that way the saved answers only reach the
         cart when the customer actually opens the quiz, never on an order where they skipped it. Only a question
         still blank is filled, so anything picked this visit wins. */
      prefillQuiz: function () {
        var saved = config.quizPrefill || {};
        var quiz = this.quiz;
        ['goal', 'orderingFor', 'mealOccasion'].forEach(function (question) {
          if (!quiz[question] && saved[question]) quiz[question] = saved[question];
        });
      },

      /** "Not sure? Take the quiz" — reopens the quiz without losing the box so far. */
      restartQuiz: function () {
        this.requestQuiz();
        this.persist();
        this.goTo('quiz');
      },

      get quizAnswered() {
        return Boolean(this.quiz.goal && this.quiz.orderingFor && this.quiz.mealOccasion);
      },

      /* Gates "Build my box" only. Deliberately loose — something@domain.tld, no spaces — because its job is to
         stop an empty or half-typed field posing as a sign-up, not to second-guess a real address. */
      get quizEmailValid() {
        return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test((this.quiz.email || '').trim());
      },

      /* `email` is optional and never blocks progress (spec §8). The profile is created WITHOUT marketing
         consent and no subscribe endpoint is ever called, so the address arrives in Klaviyo as a profile and an
         event and nothing more. The opt-in checkbox that used to sit under the field was removed on
         2026-09-28 at awais's request, and `quiz_email_consent` went with it rather than being shipped
         permanently false — a flag that says every customer refused would be worse than no flag. Nothing here
         now distinguishes someone who wants email from someone who merely typed one. */
      finishQuiz: async function (options) {
        var opts = options || {};
        // A real UTC instant: it reaches Klaviyo and the order as `quiz_completed_at`, and Shopify Flow copies it to
        // the customer. The device clock is the fallback — right to within its drift, which shop time is not.
        this.quiz.completedAt = (this.instantNow() || new Date()).toISOString();

        // "Continue without email" keeps every answer — they still reach localStorage and the cart attributes —
        // and simply drops the address. It is NOT the same as skipping the quiz.
        if (opts.withEmail === false) this.quiz.email = null;

        this.quizRequested = false;
        this.quiz.klaviyoSent = false;
        this.persist();

        var email = (this.quiz.email || '').trim();
        if (email && klaviyo.enabled) {
          var properties = this.quizProperties();
          properties.quiz_source = 'bundle_builder';
          try {
            await klaviyo.identify(email, properties);
            await klaviyo.track('Completed Quiz', email, properties);
            // Both calls answered 202. Anything short of that leaves the flag false, and the order then tells
            // Shopify Flow to send the event itself.
            this.quiz.klaviyoSent = true;
            this.persist();
          } catch (error) {
            // Never let an analytics failure block the customer.
            bbWarn('Klaviyo capture failed (the customer is not blocked):', error);
          }
        }

        this.goTo('setup');
      },

      skipQuizStep: function () {
        this.quiz.skipped = true;
        this.quizRequested = false;
        this.persist();
        this.goTo('setup');
      },

      /** Flat properties — Klaviyo does not handle nesting well (spec §8). */
      quizProperties: function () {
        var properties = {};
        if (this.quiz.goal) properties.quiz_goal = this.quiz.goal;
        if (this.quiz.orderingFor) properties.quiz_ordering_for = this.quiz.orderingFor;
        if (this.quiz.mealOccasion) properties.quiz_meal_occasion = this.quiz.mealOccasion;
        if (this.quiz.completedAt) properties.quiz_completed_at = this.quiz.completedAt;
        return properties;
      },

      /* --- Pricing (read from Liquid — never computed client-side, spec §3) --- */

      /** The merchant's stated subscription saving from theme settings, e.g. 15. Zero while the setting is blank. */
      get subscriptionDiscountPercent() {
        var percent = Number(config.subscriptionDiscountPercent);
        return percent > 0 ? percent : 0;
      },

      /** The one-time bundle's variant of the same box size — the price a subscriber's is struck against. */
      onetimeTwin: function (variant) {
        var onetime = this.bundleFor('onetime');
        if (!onetime || !variant || !variant.boxSize) return null;
        return (
          (onetime.variants || []).find(function (candidate) {
            return candidate.boxSize === variant.boxSize;
          }) || null
        );
      },

      /**
       * The subscription bundle's variants are priced at what a subscriber pays, so no plan discount is read.
       * The struck price is the one-time bundle's for the same box size, and the percentage is the merchant's
       * own figure from theme settings — shown as given, never worked out from the two prices. The two are
       * independent: no higher one-time price means nothing is struck, a blank setting means no percentage.
       *
       * `planId` is optional — the setup page needs a price before a frequency is chosen. So is `purchaseType`,
       * for a price drawn for a type other than the chosen one; it defaults to the chosen one.
       */
      priceInfo: function (variant, planId, purchaseType) {
        var subscribing = (purchaseType || this.purchaseType) === 'subscription';
        var statedPercent = subscribing ? this.subscriptionDiscountPercent : 0;

        if (!variant) return { price: 0, compareAt: 0, discountCents: 0, discountPercent: statedPercent };

        var price = variant.price;
        var compareAt = variant.compareAtPrice || 0;

        if (subscribing) {
          var plans = (variant.sellingPlans || []).filter(function (plan) {
            return plan.recurring !== false;
          });
          var wanted = planId || this.sellingPlanId;
          var allocation =
            plans.find(function (plan) {
              return plan.id === wanted;
            }) || plans[0];

          // The plan's own price is what checkout charges; with no plan discount it is the variant's price.
          if (allocation && allocation.price != null) price = allocation.price;

          var twin = this.onetimeTwin(variant);
          compareAt = twin ? twin.price : 0;
        }

        // A compare-at at or below the price is not a discount — treat it as absent rather than showing 0% off.
        if (!compareAt || compareAt <= price) compareAt = price;

        var discountCents = compareAt - price;
        // A one-time box has no stated figure, so its own compare-at, if it has one, is still worked out.
        var derivedPercent = compareAt > 0 ? Math.round((discountCents / compareAt) * 100) : 0;
        return {
          price: price,
          compareAt: compareAt,
          discountCents: discountCents,
          discountPercent: subscribing ? statedPercent : derivedPercent
        };
      },

      get currentPriceInfo() {
        return this.priceInfo(this.currentVariant);
      },

      get bundlePriceCents() {
        return this.currentPriceInfo.price;
      },

      get bundleCompareAtCents() {
        return this.currentPriceInfo.compareAt;
      },

      get bundleDiscountCents() {
        return this.currentPriceInfo.discountCents;
      },

      get hasDiscount() {
        return this.bundleDiscountCents > 0;
      },

      get bundlePrice() {
        return formatMoney(this.bundlePriceCents, config.moneyFormat);
      },

      get bundleDiscount() {
        return formatMoney(this.bundleDiscountCents, config.moneyFormat);
      },

      get discountPercent() {
        return this.currentPriceInfo.discountPercent;
      },

      /** "Save 15%" — only ever rendered when there is a percentage to name. */
      get savingsLabel() {
        if (!this.discountPercent) return '';
        return interpolate(config.strings.savePercent, { percent: this.discountPercent });
      },

      /* Hi-fi labels. The short uppercase strings the rail and cards are assembled from. Each answers '' when
         the thing it describes has not been chosen yet, so an unanswered step renders nothing at all
         rather than a half-finished sentence. */

      /** "16.90 / MEAL" — the unit price under a box-size tile. The design drops the currency here. */
      sizePerMeal: function (variant) {
        if (!variant || !variant.boxSize) return '';
        var info = this.priceInfo(variant, null, this.displayType);
        return interpolate(this.str('perMealShort'), {
          amount: formatMoney(roundToPriceStep(info.price / variant.boxSize), amountOnlyFormat)
        });
      },

      /** "6 MEALS" — the setup rail's headline. */
      get boxHeadline() {
        var count = this.mealsRequired || this.boxSize;
        if (!count) return this.str('noSizeYet');
        return interpolate(config.strings.summaryMeals, { count: count });
      },

      /** "4 OF 6 MEALS" — the meals rail's headline. */
      get mealsHeadline() {
        if (!this.mealsRequired) return this.str('noSizeYet');
        return interpolate(this.str('mealsOfTotal'), {
          selected: this.mealsCount,
          total: this.mealsRequired
        });
      },

      /** "Subscription · 15% off" / "One-time box". */
      get typeSummaryLine() {
        if (!this.purchaseType) return '';
        var label =
          this.purchaseType === 'subscription' ? this.str('typeSubscriptionLabel') : this.str('typeOnetimeLabel');
        if (this.purchaseType === 'subscription' && this.discountPercent) {
          // Either half can be a cleared setting, so join only what is there.
          return [label, interpolate(config.strings.percentOff, { percent: this.discountPercent })]
            .filter(Boolean)
            .join(' · ');
        }
        return label;
      },

      /* The gift as one line rather than a thumbnail and title, which is how the phone's box sheet lists it. */
      get giftSummaryLine() {
        if (!this.gift) return '';
        return [this.gift.title, this.str('freeGift')].filter(Boolean).join(' · ');
      },

      /** "6-meal box". */
      get sizeSummaryLine() {
        var count = this.mealsRequired || this.boxSize;
        if (!count) return '';
        return interpolate(this.str('boxOfSize'), { count: count });
      },

      /* Blank until the cadence is actually known. */
      get deliveryNote() {
        if (this.purchaseType === 'onetime') return this.str('oneTimePurchase');
        var plan = this.selectedPlan;
        return plan && plan.chargeNote ? plan.chargeNote : '';
      },

      /** "2 EMPTY SLOTS" — only while the box has room. */
      get emptySlotsLabel() {
        if (!this.mealsRequired || this.mealsRemaining <= 0) return '';
        return interpolate(this.str('emptySlots'), { count: this.mealsRemaining });
      },

      /** "MENU FOR THE WEEK OF 8 SEPTEMBER" — the week the chosen dishes will be cooked for. */
      get menuWeekLabel() {
        if (!this.deliveryDate) return '';
        return interpolate(this.str('menuForWeekOf'), {
          date: formatDayMonth(parseFloating(this.deliveryDate), config.dateLocale)
        });
      },

      /* The same label in three pieces around the date, so the phone can put the date on a line of its own the
         way its frame does. Split on the placeholder rather than the rendered text, so any wording the merchant
         gives the string still divides at the date. */
      get menuWeekParts() {
        if (!this.deliveryDate) return { before: '', date: '', after: '' };
        var parts = String(this.str('menuForWeekOf') || '').split(/\{\{\s*date\s*\}\}|\[date\]/);
        return {
          before: parts[0] || '',
          date: formatDayMonth(parseFloating(this.deliveryDate), config.dateLocale),
          after: parts.slice(1).join('')
        };
      },

      get deliveryWeekdayLabel() {
        if (!this.deliveryDate) return '';
        return formatWeekday(parseFloating(this.deliveryDate), config.dateLocale);
      },

      /* Only weekly cadences get the weekday phrasing, because that is the only case where a weekday is the
         whole story. Anything else keeps the plan's own wording rather than us inventing a phrase. */
      planCadenceLabel: function (plan) {
        if (!plan) return '';
        var weekday = this.deliveryWeekdayLabel;
        var interval = plan.interval;
        if (!weekday || !interval || interval.unit !== 'week') return plan.recurrenceLabel || '';
        if (interval.count === 1) return interpolate(this.str('everyWeekday'), { weekday: weekday });
        if (interval.count === 2) return interpolate(this.str('everyOtherWeekday'), { weekday: weekday });
        return plan.recurrenceLabel || '';
      },

      /** "6 meals every Tuesday · CHF 101.40" — the sub-line on a frequency card. */
      planDetailLine: function (plan) {
        if (!plan) return '';
        var parts = [];
        var count = this.mealsRequired || this.boxSize;
        var cadence = this.planCadenceLabel(plan);

        if (count && cadence) parts.push(interpolate(this.str('mealsEvery'), { count: count, cadence: cadence }));
        else if (count) parts.push(interpolate(config.strings.summaryMeals, { count: count }));
        else if (cadence) parts.push(cadence);

        // No price here: it is the same on both cadences, so repeating it twice under "How often?" says nothing
        // and only competes with the total in the rail.
        return parts.join(' · ');
      },

      /** "6-meal box · weekly subscription" — the review page's plan recap (2186:5908). It used to lowercase the
          frequency card's label and read "6-meal box · every week"; the plain label is only the fallback now,
          for a cadence with no wording of its own. */
      get reviewPlanTitle() {
        var parts = [];
        if (this.sizeSummaryLine) parts.push(this.sizeSummaryLine);

        var plan = this.selectedPlan;
        var planName = plan ? plan.subscriptionLabel || (plan.label && plan.label.toLowerCase()) : '';
        if (this.purchaseType === 'subscription' && planName) parts.push(planName);
        else if (this.purchaseType === 'onetime') parts.push(config.strings.typeOnetime);

        return parts.filter(Boolean).join(' · ');
      },

      /* The discount line is dropped entirely when no percentage is set, rather than printing "0% applied". */
      get reviewPlanNotes() {
        if (this.purchaseType !== 'subscription') {
          return [this.str('planNoteOnetime')].filter(Boolean);
        }

        var notes = [];
        if (this.discountPercent) notes.push(interpolate(this.str('planNote1'), { percent: this.discountPercent }));
        notes.push(this.str('planNote2'));
        notes.push(this.str('planNote3'));
        return notes.filter(Boolean);
      },

      /** The headline on a purchase-type card: "Save 15% on every box" / "Full price". */
      typeHeadline: function (type) {
        if (type !== 'subscription') return this.str('fullPriceHeadline');
        var info = this.perMealInfo('subscription');
        if (!info || !info.discountPercent) return this.str('subscriptionHeadline');
        return interpolate(this.str('saveOnEveryBox'), { percent: info.discountPercent });
      },

      /* Doubles as the reason the step is unfinished, so a customer never meets a dead button. */
      railCtaLabel: function (nextStep) {
        if (nextStep === 'review') {
          // On the meals page the only thing that can be missing is meals, and the design says so in the button
          // itself — "Add 2 more meals" — rather than in a separate line of small print.
          if (this.mealsRequired && this.mealsRemaining > 0) {
            return interpolate(this.str('addMoreMealsCta'), { count: this.mealsRemaining });
          }
          if (!this.canContinue) return this.continueBlockedReason || this.str('continue');
          return this.str('continueToReview');
        }

        // On the setup page several different things can be missing, so the button names whichever it is.
        if (!this.canContinue) return this.continueBlockedReason || this.str('continue');

        var count = this.mealsRequired || this.boxSize;
        if (!count) return this.str('continue');
        return interpolate(this.str('chooseMyMeals'), { count: count });
      },

      /* --- Per-meal pricing, for the purchase-type and box-size cards ---------------------------------- */

      /* Falls back to the smallest size before one is chosen, so the type cards are never blank. */
      perMealInfo: function (purchaseType) {
        var bundle = this.bundleFor(purchaseType);
        if (!bundle) return null;

        var size = this.boxSize;
        var variant = null;
        if (size) {
          variant = bundle.variants.find(function (item) {
            return item.boxSize === size;
          });
        }
        if (!variant) {
          variant = bundle.variants
            .filter(function (item) {
              return item.boxSize > 0;
            })
            .slice()
            .sort(function (a, b) {
              return a.boxSize - b.boxSize;
            })[0];
        }
        if (!variant || !variant.boxSize) return null;

        var info = this.priceInfo(variant, null, purchaseType);

        return {
          price: roundToPriceStep(info.price / variant.boxSize),
          compareAt: roundToPriceStep(info.compareAt / variant.boxSize),
          discountPercent: info.discountPercent,
          boxSize: variant.boxSize
        };
      },

      perMealPrice: function (purchaseType) {
        var info = this.perMealInfo(purchaseType);
        return info ? formatMoney(info.price, config.moneyFormat) : '';
      },

      perMealCompareAt: function (purchaseType) {
        var info = this.perMealInfo(purchaseType);
        if (!info || info.compareAt <= info.price) return '';
        return formatMoney(info.compareAt, config.moneyFormat);
      },

      /* --- Box size tiles ------------------------------------------------------------------------------- */

      sizePrice: function (variant) {
        return formatMoney(this.priceInfo(variant, null, this.displayType).price, config.moneyFormat);
      },

      sizeCompareAt: function (variant) {
        var info = this.priceInfo(variant, null, this.displayType);
        if (info.compareAt <= info.price) return '';
        return formatMoney(info.compareAt, config.moneyFormat);
      },

      /* The badge rides on the variant, set per size in theme settings and read by the homepage section from
         the same place, so the two can never disagree about which size carries which tag. */
      sizeNudgeFor: function (variant) {
        return (variant && variant.badge) || '';
      },

      /* True when this add-on goes out with every delivery. A recurring mode left over from before a switch to a
         one-time box, or on an add-on with no plan at this cadence, does not count. */
      addonRecurs: function (addon) {
        return addon.mode === 'subscription' && this.addonSupportsRecurring(addon.variantId);
      },

      /* What one of this add-on costs as it sits in the box. On every delivery it is charged at its selling
         plan's price, which checkout uses and which can differ from the one-off price. */
      addonUnitCents: function (addon) {
        var entry = (config.addons || {})[String(addon.variantId)];
        if (!entry) return 0;
        if (this.addonRecurs(addon)) {
          var plan = this.addonPlanFor(addon.variantId);
          if (plan && plan.price != null) return plan.price;
        }
        return entry.price || 0;
      },

      get addonsSubtotalCents() {
        var self = this;
        return this.addons.reduce(function (total, addon) {
          return total + self.addonUnitCents(addon) * addon.quantity;
        }, 0);
      },

      /* Each add-on by name, with its own price and whether it comes once or with every box — shown in the
         summary and in the box list rather than one "Add-ons" total (client feedback, 2026-10-12). */
      get addonLines() {
        var self = this;
        var addons = config.addons || {};
        return this.addons.map(function (addon) {
          var entry = addons[String(addon.variantId)];
          var recurring = self.addonRecurs(addon);
          return {
            variantId: addon.variantId,
            // This render's title first, so it follows the page's language rather than the one it was added in.
            title: (entry && entry.title) || addon.title,
            quantity: addon.quantity,
            recurring: recurring,
            modeLabel: recurring ? config.strings.addonEveryBox : config.strings.addonOneTime,
            price: formatMoney(self.addonUnitCents(addon) * addon.quantity, config.moneyFormat)
          };
        });
      },

      /* The review page's dish list. No prices: the bundle is fixed-price, so a meal has none of its own, and the
         client wanted the box's value in the summary rather than a full per-meal figure on every line
         (2026-10-12). It used to share the compare-at out across the lines. */
      get mealLines() {
        return this.meals.map(function (meal) {
          return { variantId: meal.variantId, title: meal.title, quantity: meal.quantity };
        });
      },

      get giftLine() {
        if (!this.gift) return null;
        return {
          variantId: this.gift.variantId,
          title: this.gift.title,
          quantity: 1,
          linePrice: formatMoney(0, config.moneyFormat)
        };
      },

      /* `discount` is null when there is nothing to show, so the row hides rather than printing "− CHF 0.00". The
         row names the percentage beside the amount, so it needs both: a blank setting hides it rather than
         printing "0%", and the struck total still carries the saving. */
      get orderSummary() {
        var info = this.currentPriceInfo;
        var addons = this.addonsSubtotalCents;
        var showDiscount = info.discountCents > 0 && info.discountPercent > 0;

        return {
          subtotalLabel: interpolate(config.strings.summaryMeals, { count: this.mealsRequired }),
          subtotal: formatMoney(info.compareAt, config.moneyFormat),
          discount: showDiscount ? formatMoney(info.discountCents, config.moneyFormat) : null,
          discountLabel: showDiscount ? interpolate(config.strings.percentOff, { percent: info.discountPercent }) : '',
          hasGift: Boolean(this.gift),
          gift: formatMoney(0, config.moneyFormat),
          total: formatMoney(info.price + addons, config.moneyFormat),
          // Struck beside the total, so it has to carry the same add-ons: only the box is discounted, and the box
          // alone read as a smaller saving than was taken off (awais, 2026-10-12).
          totalCompareAt: formatMoney(info.compareAt + addons, config.moneyFormat)
        };
      },

      get totalCents() {
        return this.bundlePriceCents + this.addonsSubtotalCents;
      },

      get total() {
        return formatMoney(this.totalCents, config.moneyFormat);
      },

      money: function (cents) {
        return formatMoney(cents, config.moneyFormat);
      },

      /* --- Validation (spec §5) --- */

      get validationErrors() {
        var errors = [];

        if (!this.purchaseType) errors.push('purchase_type');
        if (!this.boxSize) errors.push('box_size');
        if (!this.currentVariant) errors.push('variant');
        else if (!this.currentVariant.available) errors.push('variant_unavailable');

        // The Recharge rules are the only source of the meals count — never guess one.
        if (!this.dataReady) errors.push('bundle_data');
        if (!this.mealsLimits) errors.push('bundle_limits_unknown');

        // 1. Meals must exactly equal the box size — not fewer, not more.
        if (!this.mealsRequired || this.mealsCount !== this.mealsRequired) errors.push('meals_count');

        if (this.purchaseType === 'subscription') {
          // 3. Subscription requires exactly one gift. 5. A frequency (selling plan) is required.
          if (!this.gift) errors.push('gift_required');
          if (!this.sellingPlanId) errors.push('selling_plan_required');
          else if (!this.selectedPlan) errors.push('selling_plan_invalid');
        } else if (this.purchaseType === 'onetime') {
          // 4. One-time must have no gift and no selling plan — the product has neither.
          if (this.gift) errors.push('gift_not_allowed');
          if (this.sellingPlanId) errors.push('selling_plan_not_allowed');
        }

        return errors;
      },

      get isValid() {
        return this.validationErrors.length === 0;
      },

      /* --- Add to cart (spec §10) --- */

      addToCart: async function () {
        if (this.submitting) return;

        // 1. Re-run every validation rule.
        if (!this.isValid) {
          this.fail(this.checkoutBlockedReason || config.strings.fixBeforeCheckout);
          return;
        }

        this.submitting = true;
        this.clearError();

        try {
          // 2. Recompute the delivery date. Long sessions cross the Thursday boundary.
          this.recomputeDeliveryDate('checkout');

          // 3-5. Build the Recharge bundle, validate it, mint the _rb_id — and, side by side, get the cart ready:
          //      quiz and delivery attributes written and the box already in it taken out, in one request. Neither
          //      waits on the other, and both must succeed before anything is added, so a failure on either side
          //      still leaves no half-built cart behind. Each round trip is about a second; run in a row, they
          //      were most of the wait on the Checkout button.
          var bundle = this.buildBundleObject();
          var settled = await Promise.allSettled([
            Recharge.getBundleId(bundle),
            this.prepareCart(this.cartAttributes())
          ]);
          var failure = settled.find(function (outcome) {
            return outcome.status === 'rejected';
          });
          if (failure) throw failure.reason;

          var rbId = settled[0].value;
          if (!rbId) throw new Error('RECHARGE_NO_BUNDLE_ID');

          // 6. One parent bundle line (fixed-price bundles add no child lines) plus each add-on as its own line.
          var items = this.buildCartItems(rbId, bundle);
          var added = await cart.addItems(items);

          // 8. The box is KEPT, with a note of what it put in the cart, so a customer who comes back from
          //    checkout finds it on the review page and can still change it. Written now, not on the debounce:
          //    the page is about to be left, and a pending timer would go with it.
          this.cartLink = { rbId: rbId, addons: this.addonLinesOf(items) };
          this.persistNow();

          // Belt and braces against the failure mode the spec warns about: bundle expansion fails silently.
          this.verifyCart(rbId, added, settled[1].value);

          // 9. Off to checkout — once the page has loaded, so the review page keeps its own history entry and Back
          //    returns to it; and from the top of the page, so the frozen copy the back/forward cache keeps, and
          //    the snapshot a phone shows during a swipe back, both open where the review page starts.
          this.handedToCheckout = true;
          markHandoff();
          await whenLoaded(LOAD_WAIT_MS);
          this.scrollToTop({ instant: true });
          window.location.href = config.routes.checkout;
        } catch (error) {
          // The button stays busy while the message is worked out, which can take one more round trip.
          await this.handleAddToCartError(error);
          this.submitting = false;
        }
      },

      /** The collection a pick goes to Recharge under: its own, else the configured one for its kind. */
      collectionOf: function (kind, item) {
        return String(item.collectionId || (kind === 'gift' ? config.recharge.collections.gifts : config.recharge.collections.meals));
      },

      buildBundleObject: function () {
        var self = this;
        var bundle = this.activeBundle;
        var variant = this.currentVariant;

        // Recharge's BundleSelectionAppProxy declares every id as a string.
        var selections = this.meals.map(function (meal) {
          return {
            collectionId: self.collectionOf('meal', meal),
            externalProductId: String(meal.productId),
            externalVariantId: String(meal.variantId),
            quantity: meal.quantity
          };
        });

        // Only the subscription bundle has a gift collection source.
        if (this.purchaseType === 'subscription' && this.gift) {
          selections.push({
            collectionId: this.collectionOf('gift', this.gift),
            externalProductId: String(this.gift.productId),
            externalVariantId: String(this.gift.variantId),
            quantity: 1
          });
        }

        return {
          externalProductId: String(bundle.productId),
          externalVariantId: String(variant.id),
          selections: selections
        };
      },

      buildCartItems: function (rbId, bundle) {
        var self = this;
        var variant = this.currentVariant;

        // `Contents` first and `_rb_id` second, the order Recharge's own widget writes them in.
        var properties = {};
        var contents = this.bundleContents(bundle);
        if (contents) properties[BUNDLE_CONTENTS_PROPERTY] = contents;
        // `_rb_id` is what Recharge reads on order creation to expand the bundle. Wrong or missing = silent failure.
        properties._rb_id = rbId;

        var parent = { id: variant.id, quantity: 1, properties: properties };
        // No leading underscore, so checkout and the cart show it. The `delivery_date` cart attribute stays the ISO
        // copy ops and Flow read; this one is for the customer, in their language.
        if (this.deliveryDateLabel) parent.properties[config.strings.deliveryDateProperty] = this.deliveryDateLabel;
        // Set by the recompute just before checkout, so it is the moment of the click, as the date is.
        if (this.afterCutoff) parent.properties[AFTER_CUTOFF_PROPERTY] = 'true';
        // `selling_plan` sits alongside `properties`, not inside it.
        if (this.sellingPlanId) parent.selling_plan = this.sellingPlanId;

        var items = [parent];

        // Add-ons live outside the bundle with their own subscriptions (spec §9).
        this.addons.forEach(function (addon) {
          var item = { id: addon.variantId, quantity: addon.quantity };
          if (addon.mode === 'subscription') {
            var plan = self.addonPlanFor(addon.variantId);
            if (plan) item.selling_plan = plan.id;
          }
          items.push(item);
        });

        return items;
      },

      /** "3x Poulet Pané… 2x Poulet à l’Orange et Riz 1x Minis Brownies Moelleux" — the very selections the
          `_rb_id` was minted from (meals, then the gift), in Recharge's own format, titled as this page shows them
          (Recharge's "user locale"). Every dish is single-variant, so no variant name follows the title. */
      bundleContents: function (bundle) {
        var self = this;
        var meals = this.meals;
        var gift = this.gift;
        return ((bundle && bundle.selections) || [])
          .filter(function (selection) {
            return selection.quantity > 0;
          })
          .map(function (selection) {
            var id = Number(selection.externalVariantId);
            var known = meals.find(function (meal) {
              return Number(meal.variantId) === id;
            }) || (gift && Number(gift.variantId) === id ? gift : null);
            return selection.quantity + 'x ' + self.titleFor(id, known && known.title);
          })
          .join(' ');
      },

      /** This render's title first, so a box started in another language still names its dishes as rendered now. */
      titleFor: function (variantId, fallback) {
        var card = this.cardFor(variantId);
        return (card && card.dataset.title) || fallback || String(variantId);
      },

      /* --- The box in the cart --------------------------------------------------------------------------- */

      /** The add-on lines of a cart/add.js payload, as `cartLink` records them. */
      addonLinesOf: function (items) {
        return items
          .filter(function (item) {
            return !(item.properties && item.properties._rb_id);
          })
          .map(function (item) {
            return { variantId: Number(item.id), sellingPlanId: item.selling_plan || null, quantity: item.quantity };
          });
      },

      /* One builder box per cart. Every line of either bundle product goes — whichever visit, tab or older
         theme put it there, a second box is the duplicate the client reported — and the add-ons this box added
         last time are taken back off by quantity. They are plain lines, so one can share its line with the same
         product added from a product page; taking back only what this box put in leaves the rest of it alone.

         The cart attributes ride on the same request. Resolves to the cart as update.js returns it.

         Throws on a cart it cannot read: adding without removing is exactly the duplicate this exists to stop,
         so the customer gets the usual error and Retry instead. */
      prepareCart: async function (attributes) {
        var current = await cart.get();
        if (!current || !Array.isArray(current.items)) {
          var readError = new Error('CART_ERROR');
          readError.detail = null;
          throw readError;
        }

        var bundleProductIds = ['subscription', 'onetime']
          .map(function (which) {
            var bundle = config.bundles && config.bundles[which];
            return bundle ? Number(bundle.productId) : 0;
          })
          .filter(Boolean);

        var updates = {};
        current.items.forEach(function (item) {
          if (bundleProductIds.indexOf(Number(item.product_id)) !== -1) updates[item.key] = 0;
        });

        var link = this.cartLink;
        ((link && link.addons) || []).forEach(function (addon) {
          var line = current.items.find(function (item) {
            if (item.key in updates) return false;
            if (Number(item.variant_id) !== Number(addon.variantId)) return false;
            if (item.properties && Object.keys(item.properties).length) return false;
            var allocation = item.selling_plan_allocation;
            var planId = allocation && allocation.selling_plan ? Number(allocation.selling_plan.id) : null;
            return planId === (addon.sellingPlanId ? Number(addon.sellingPlanId) : null);
          });
          if (line) updates[line.key] = Math.max(0, line.quantity - (Number(addon.quantity) || 0));
        });

        var body = { attributes: attributes };
        if (Object.keys(updates).length) body.updates = updates;
        var updated = await cart.update(body);

        // Nothing in the cart is this box's any more. Dropped and saved at once, so an add that fails after this
        // leaves a plain saved box, not a link the next visit would read as "bought" and throw away.
        if (this.cartLink) {
          this.cartLink = null;
          this.persistNow();
        }

        return updated;
      },

      /* Whether this box's bundle line is still in the cart: true, false, or null when the cart could not be
         read — and an unanswered question never throws a customer's box away. */
      isLinkInCart: async function (link) {
        if (!link || !link.rbId) return null;
        var current = null;
        try {
          current = await cart.get(CART_CHECK_TIMEOUT_MS);
        } catch (error) {
          current = null;
        }
        if (!current || !Array.isArray(current.items)) return null;
        return current.items.some(function (item) {
          return item.properties && item.properties._rb_id === link.rbId;
        });
      },

      /** Cart-level attributes so they land on the order and stay readable by Shopify Flow and ops (spec §10). */
      cartAttributes: function () {
        var attributes = { delivery_date: this.deliveryDate };
        var quiz = this.quizProperties();
        Object.keys(quiz).forEach(function (key) {
          // Omit keys with no value rather than writing an empty string.
          if (quiz[key]) attributes[key] = quiz[key];
        });
        // Only for a finished quiz, and always as a word. "false" is Shopify Flow's cue to send the `Completed Quiz`
        // event to Klaviyo under the checkout email, because the browser did not: no email was typed, capture is
        // off, or the call failed.
        if (this.quiz.completedAt) attributes.quiz_sent_to_klaviyo = this.quiz.klaviyoSent ? 'true' : 'false';
        return attributes;
      },

      /* Read off the two responses already in hand — add.js returns the lines it added, update.js the cart with
         its attributes — rather than fetching the cart a third time just to log. Diagnostic only. */
      verifyCart: function (rbId, added, updated) {
        try {
          var parentLine = ((added && added.items) || []).find(function (item) {
            return item.properties && item.properties._rb_id === rbId;
          });
          if (!parentLine) {
            bbError(
              '_rb_id is NOT on the added bundle line — Recharge will not expand this bundle and the order will look',
              'fine in the cart while containing none of the selections. cart/add.js returned:',
              added
            );
          } else if (!parentLine.properties[BUNDLE_CONTENTS_PROPERTY]) {
            bbWarn(BUNDLE_CONTENTS_PROPERTY + ' is not on the added bundle line, which Recharge documents as required.', parentLine);
          }

          if (!updated || !updated.attributes || !updated.attributes.delivery_date) {
            bbWarn('delivery_date attribute did not stick on the cart. Cart attributes:', updated && updated.attributes);
          }
        } catch (error) {
          // Verification is diagnostic only; never block checkout on it.
        }
      },

      /** Keep the customer's state, explain it plainly, offer retry (spec §10). Never reset the builder. */
      handleAddToCartError: async function (error) {
        var code = error && error.message;
        bbError('add to cart failed:', error);

        /* When Recharge turns the box down, a dish that has gone since it was picked is ruled out first. It cannot
           be bought, and once its card has left the menu the customer has no obvious way to remove it (only the box
           column on the meals page still lists it), so it is taken out here and named, the way Shopify names a
           sold-out add-on — with a way to the replacements instead of Retry, which would only fail again. A cart
           error is Shopify's own and already says what is wrong.

           No answer in time says nothing about the box, so the customer is not sent to check their meals: they
           are told the connection seems slow and offered Retry, which runs the whole checkout again in place. */
        if (code !== 'CART_ERROR' && code !== 'RECHARGE_SDK_MISSING') {
          try {
            var unavailable = await this.unavailableItems();
            if (unavailable.length) {
              this.removeFromBox(unavailable);
              this.fail('', { removed: unavailable });
              return;
            }
          } catch (lookupError) {
            this.fail(config.strings.errorSlowConnection, { retry: true });
            return;
          }
        }

        var message = config.strings.errorGeneric;
        if (code === 'RECHARGE_SDK_MISSING') message = config.strings.errorRechargeMissing;
        else if (code === 'RECHARGE_VALIDATION_FAILED') message = config.strings.errorBundleInvalid;
        else if (code === 'CART_ERROR') message = error.detail || config.strings.errorCart;

        this.fail(message, { retry: true });
      },

      /* The box's dishes, and its gift, that Recharge no longer offers, as `{ kind, variantId, title }`. Empty when
         every one is still on offer or that cannot be told; rejects with TIMEOUT when the answer is too slow. */
      unavailableItems: async function () {
        var bundle;
        try {
          bundle = this.buildBundleObject();
        } catch (error) {
          return [];
        }

        var selections = await Recharge.unavailableSelections(bundle, AVAILABILITY_CHECK_TIMEOUT_MS);
        if (!selections || !selections.length) return [];

        // A pick is its collection and variant together: the same dish could be a meal and the gift.
        var self = this;
        var keys = selections.map(function (selection) {
          return String(selection.collectionId) + ':' + Number(selection.externalVariantId);
        });
        var isFlagged = function (kind, item) {
          return keys.indexOf(self.collectionOf(kind, item) + ':' + Number(item.variantId)) !== -1;
        };

        var items = this.meals
          .filter(function (meal) {
            return isFlagged('meal', meal);
          })
          .map(function (meal) {
            return { kind: 'meal', variantId: Number(meal.variantId), title: meal.title };
          });
        var gift = this.purchaseType === 'subscription' ? this.gift : null;
        if (gift && isFlagged('gift', gift)) {
          items.push({ kind: 'gift', variantId: Number(gift.variantId), title: gift.title });
        }
        return items;
      },

      /** Takes `{ kind, variantId }` items out of the box: every one of a meal, or the gift. */
      removeFromBox: function (items) {
        var self = this;
        items.forEach(function (item) {
          if (item.kind === 'gift') {
            if (self.gift && Number(self.gift.variantId) === item.variantId) self.gift = null;
            return;
          }
          self.meals = self.meals.filter(function (meal) {
            return Number(meal.variantId) !== item.variantId;
          });
        });
        this.persist();
      },

      /** "Sorry, “A” and “B” are no longer available, so we’ve taken them out of your box. …" */
      unavailableMessage: function (items) {
        var self = this;
        var quoted = function (kind) {
          return items
            .filter(function (item) {
              return item.kind === kind;
            })
            .map(function (item) {
              return self.str('quotedTitle', { title: self.titleFor(item.variantId, item.title) });
            });
        };

        var meals = quoted('meal');
        var gifts = quoted('gift');
        var parts = [];
        if (meals.length) {
          parts.push(
            this.str(meals.length === 1 ? 'mealUnavailable' : 'mealsUnavailable', {
              titles: joinList(meals, config.dateLocale)
            })
          );
        }
        if (gifts.length) parts.push(this.str('giftUnavailable', { title: gifts[0] }));
        return parts.join(' ');
      },

      retryAddToCart: function () {
        this.clearError();
        this.addToCart();
      },

      /* The only message the builder shows, beside the checkout button rather than at the top of the page,
         because that is where the customer is looking when it happens. */

      fail: function (message, options) {
        this.error = {
          message: message || config.strings.errorGeneric,
          retry: Boolean(options && options.retry),
          removed: (options && options.removed) || null
        };
      },

      /* What the error box says. Dishes taken out for being no longer available stay named until the box is whole
         again, because choosing their replacements is what is left to do. */
      get errorMessage() {
        var error = this.error;
        if (!error) return '';
        if (!error.removed) return error.message;
        return this.isValid ? '' : this.unavailableMessage(error.removed);
      },

      /* Where the error box's button leads once dishes were taken out: the box page when the gift went, since it
         is chosen there and comes first, otherwise the dishes. */
      get errorFixStep() {
        var error = this.error;
        if (!error || !error.removed) return null;
        var giftWent = error.removed.some(function (item) {
          return item.kind === 'gift';
        });
        return giftWent ? 'setup' : 'meals';
      },

      clearError: function () {
        this.error = null;
      },

      /* --- Persistence (spec §4) --- */

      persist: function () {
        var self = this;
        if (persistTimer) clearTimeout(persistTimer);
        persistTimer = setTimeout(function () {
          persistTimer = null;
          self.writeStorage();
        }, PERSIST_DEBOUNCE_MS);
      },

      /** The same write without the debounce, for a moment the page may be left straight after. */
      persistNow: function () {
        if (persistTimer) clearTimeout(persistTimer);
        persistTimer = null;
        this.writeStorage();
      },

      writeStorage: function () {
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              v: STORAGE_SCHEMA_VERSION,
              step: this.step,
              quiz: this.quiz,
              // Which question they are on, so a refresh mid-quiz does not send them back to the first one.
              // The answers alone were already kept, but they had to click through to their place again.
              quizPage: this.quizPage,
              purchaseType: this.purchaseType,
              boxSize: this.boxSize,
              sellingPlanId: this.sellingPlanId,
              gift: this.gift,
              meals: this.meals,
              addons: this.addons,
              cartLink: this.cartLink
              // deliveryDate is intentionally NOT persisted — always recomputed.
            })
          );
        } catch (error) {
          // Safari private mode refuses writes. Losing persistence is acceptable; crashing is not.
        }
      },

      /** `saved` is what `readSaved` returned — read before boot, because a box in the cart is checked first. */
      restore: function (saved) {
        if (!saved || saved.v !== STORAGE_SCHEMA_VERSION) return;

        if (saved.quiz) Object.assign(this.quiz, saved.quiz);
        if (saved.quizPage != null) {
          var page = parseInt(saved.quizPage, 10);
          this.quizPage = page >= 0 && page <= QUIZ_LAST_PAGE ? page : 0;
        }
        if (saved.purchaseType) this.purchaseType = saved.purchaseType;
        if (saved.boxSize) this.boxSize = saved.boxSize;
        if (saved.sellingPlanId) this.sellingPlanId = saved.sellingPlanId;
        if (saved.gift) this.gift = saved.gift;
        if (Array.isArray(saved.meals)) this.meals = saved.meals;
        if (Array.isArray(saved.addons)) this.addons = saved.addons;
        if (saved.cartLink && saved.cartLink.rbId) this.cartLink = saved.cartLink;
        if (saved.step) {
          this.requestedStep = normaliseStep(saved.step) || 'setup';
          this.step = this.requestedStep;
        }
      },

      /** Guard against an inconsistent restored or deep-linked state. */
      normalise: function () {
        if (this.purchaseType !== 'onetime' && this.purchaseType !== 'subscription') this.purchaseType = null;

        if (this.purchaseType === 'onetime') {
          this.sellingPlanId = null;
          this.gift = null;
          this.addons.forEach(function (addon) {
            addon.mode = 'onetime';
          });
        }

        // A saved size the active bundle no longer offers cannot be trusted.
        if (!this.boxSizeExists(this.boxSize)) this.boxSize = null;
      },

      clearStorage: function () {
        if (persistTimer) clearTimeout(persistTimer);
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          /* ignore */
        }
      },

      /* --- Utilities --- */

      interpolate: interpolate,

      /** A string from config, guaranteed to be a string. A missing key renders as empty, never "undefined". */
      str: function (key, values) {
        var template = config.strings[key];
        if (!template) return '';
        return values ? interpolate(template, values) : template;
      },

      /* Lives here rather than inline in the FAQ section so the rule has one home.

         `quiz` alone matches every quiz page; `quiz:<question>` only the page asking that question. Named, not
         numbered, because the merchant can reorder the questions — a numbered FAQ would silently end up beside
         a different question. */
      stepMatchesFaq: function (target) {
        if (!target || target === 'all') return true;
        var parts = target.split(':');
        if (this.step !== parts[0]) return false;
        return !parts[1] || parts[1] === this.quizQuestion;
      },

      /** The question on the quiz page now showing, e.g. `goal`. Falls back to the shipped order. */
      get quizQuestion() {
        var order = config.quizQuestions || ['goal', 'orderingFor', 'mealOccasion'];
        return order[this.quizPage] || null;
      },

      label: function (key) {
        return config.strings[key] || '';
      },

    };
  }

  /* --- Boot --- */

  document.addEventListener('alpine:init', function () {
    var config = readConfig();
    if (!config) return;
    window.Alpine.store('builder', buildStore(config));
  });

  // The theme editor swaps a section's markup without reloading the page. Alpine only scans once, on boot, so the
  // replaced DOM would come back inert and every merchant edit would look like a broken builder. Rebuild the store
  // from the fresh config and re-scan just that section.
  document.addEventListener('shopify:section:load', function (event) {
    if (!window.Alpine || !event.target || !event.target.querySelector) return;
    if (!event.target.querySelector('[data-bundle-builder]')) return;

    var config = readConfig();
    if (config) window.Alpine.store('builder', buildStore(config));
    window.Alpine.initTree(event.target);
  });

  /* Test surface: `TLCBundleBuilder.testDeliveryDates()` checks the §7 acceptance cases from the console
     without a build step. */

  window.TLCBundleBuilder = {
    nextDeliveryDate: nextDeliveryDate,
    formatMoney: formatMoney,
    parseFloating: parseFloating,
    toISODate: toISODate,
    parseInterval: parseInterval,
    intervalKey: intervalKey,

    testDeliveryDates: function () {
      // Eastern Time, with the default 15:00 cut-off.
      var cases = [
        { ordered: '2025-09-01T10:00', charge: '2025-09-04', delivery: '2025-09-09', flag: false, note: 'Mon 1 Sep' },
        { ordered: '2025-09-03T16:00', charge: '2025-09-04', delivery: '2025-09-09', flag: false, note: 'Wed 3 Sep 16:00 — the cut-off is the charge day\'s only' },
        { ordered: '2025-09-04T10:00', charge: '2025-09-04', delivery: '2025-09-09', flag: false, note: 'Thu 4 Sep — the charge day still counts' },
        { ordered: '2025-09-04T14:59', charge: '2025-09-04', delivery: '2025-09-09', flag: false, note: 'Thu 4 Sep 14:59 — last minute before the cut-off' },
        { ordered: '2025-09-04T15:00', charge: '2025-09-11', delivery: '2025-09-16', flag: true, note: 'Thu 4 Sep 15:00 — the cut-off: next week, flagged' },
        { ordered: '2025-09-04T23:59', charge: '2025-09-11', delivery: '2025-09-16', flag: true, note: 'Thu 4 Sep 23:59 — still flagged' },
        { ordered: '2025-09-05T00:00', charge: '2025-09-11', delivery: '2025-09-16', flag: false, note: 'Fri 5 Sep — the next week begins, Recharge skips by itself' },
        { ordered: '2025-09-07T10:00', charge: '2025-09-11', delivery: '2025-09-16', flag: false, note: 'Sun 7 Sep' },
        { ordered: '2025-09-09T10:00', charge: '2025-09-11', delivery: '2025-09-16', flag: false, note: 'Tue 9 Sep' },
        { ordered: '2025-09-01T10:00', charge: '2025-09-11', delivery: '2025-09-16', flag: true, tester: true, note: 'Mon 1 Sep, test customer — as if past the cut-off' },
        { ordered: '2025-09-04T16:00', charge: '2025-09-11', delivery: '2025-09-16', flag: true, tester: true, note: 'Thu 4 Sep 16:00, test customer — moved one week, not two' }
      ];

      var results = cases.map(function (testCase) {
        var actual = nextDeliveryDate(parseFloating(testCase.ordered), {
          cutoffMinutes: DEFAULT_CUTOFF_MINUTES,
          cutoffTester: testCase.tester === true
        });
        var actualCharge = toISODate(actual.charge);
        var actualDelivery = toISODate(actual.delivery);
        return {
          case: testCase.note,
          expectedCharge: testCase.charge,
          actualCharge: actualCharge,
          expectedDelivery: testCase.delivery,
          actualDelivery: actualDelivery,
          expectedFlag: testCase.flag,
          actualFlag: actual.afterCutoff,
          pass:
            actualCharge === testCase.charge &&
            actualDelivery === testCase.delivery &&
            actual.afterCutoff === testCase.flag
        };
      });

      if (console.table) console.table(results);
      var failed = results.filter(function (row) {
        return !row.pass;
      });
      console.log(
        failed.length ? '❌ ' + failed.length + ' delivery-date case(s) FAILED' : '✅ all delivery-date cases pass'
      );
      return results;
    }
  };
})();
